import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import RecordingModal from './RecordingModal';
import useIssueOrdering from '../hooks/useIssueOrdering';
import { useIssueFeed } from '../hooks/useIssueFeed';
import { fetchAssignableUsers, fetchIssue, fetchIssueTypes, updateIssue } from '../api/issues';
import RichTextHtml from './RichTextHtml';
import { safeUrl } from '../utils/safeUrl';
import { issueCacheSingleton } from '../cache/issueCache';
import './JiraDashboard.css';

const VIEW_MODES = {
  MY: 'My',
  OTHERS: 'Others',
  RECENT: 'Recent',
  BOARD: 'Board'
};

// Toggle obfuscation on/off for screen recording
const ENABLE_OBFUSCATION = false;

// HTML-escape user-controlled strings before concatenating them into the
// markup we feed into innerHTML. Without this, ADF text like "<MyComponent>"
// would either be interpreted as a tag (and then stripped by the sanitizer)
// or — worse — be a vector for stored XSS. SECURITY_AUDIT C-1.
const ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ESC_MAP[c]);
}

// CSS color allowlist for textColor marks. Permits #hex (3, 4, 6, 8 digit),
// rgb()/rgba(), and a small set of named colors. Anything else returns ''.
const NAMED_COLORS = new Set(['black','white','red','green','blue','yellow','orange','purple','gray','grey']);
function safeColor(c) {
  const s = String(c == null ? '' : c).trim().toLowerCase();
  if (/^#[0-9a-f]{3,8}$/.test(s)) return s;
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*(?:0|1|0?\.\d+)\s*)?\)$/.test(s)) return s;
  if (NAMED_COLORS.has(s)) return s;
  return '';
}

/**
 * Obfuscate text by showing only first 3 and last 3 characters
 * @param {string} text - Text to obfuscate
 * @returns {string} Obfuscated text
 */
function obfuscate(text) {
  if (!ENABLE_OBFUSCATION) return text;
  if (!text || typeof text !== 'string') return text;
  if (text.length <= 6) return text; // Too short to obfuscate
  const first3 = text.substring(0, 3);
  const last3 = text.substring(text.length - 3);
  const middleLength = text.length - 6;
  const asterisks = '*'.repeat(middleLength);
  return `${first3}${asterisks}${last3}`;
}

export default function JiraDashboard({ user, returnToView, onReturnToView }) {
  const [viewMode, setViewMode] = useState(VIEW_MODES.MY);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [smartSearchIds, setSmartSearchIds] = useState(null); // null = not active, array = filtered IDs
  const [smartSearchLoading, setSmartSearchLoading] = useState(false);
  const [filters, setFilters] = useState({ status: [], assignee: [], priority: [], type: [] });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [issueLoading, setIssueLoading] = useState(false); // Loading state when fetching issue from API
  const [issueError, setIssueError] = useState(null); // Error state when issue fetch fails (404, no access)
  const needToKnowUsers = useMemo(() => {
    const fieldId = process.env.REACT_APP_JIRA_NEED_TO_KNOW_FIELD;
    const value = fieldId ? selectedIssue?.fields?.[fieldId] : null;
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [selectedIssue]);
  const [expandedCards, setExpandedCards] = useState({}); // Track which cards are expanded in issue preview
  const [issueComments, setIssueComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentsStartAt, setCommentsStartAt] = useState(0);
  const [recordingPopupOpen, setRecordingPopupOpen] = useState(false);
  const [recordingContext, setRecordingContext] = useState(null);
  const [processingTransaction, setProcessingTransaction] = useState(false);
  const [pinnedIssues, setPinnedIssues] = useState([]);

  // Toggle card expansion in issue preview
  const toggleCard = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  // Parent work item popup state
  const [parentPopupOpen, setParentPopupOpen] = useState(false);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [parentSearchResults, setParentSearchResults] = useState([]);

  // Issue editing popups
  const [assigneePopupOpen, setAssigneePopupOpen] = useState(false);
  const [priorityPopupOpen, setPriorityPopupOpen] = useState(false);
  const [dueDatePopupOpen, setDueDatePopupOpen] = useState(false);
  const [issueTypePopupOpen, setIssueTypePopupOpen] = useState(false);
  const [needToKnowPopupOpen, setNeedToKnowPopupOpen] = useState(false);
  const [needToKnowSelected, setNeedToKnowSelected] = useState([]); // Array of account IDs
  const [issueSaving, setIssueSaving] = useState(false);
  const [issueTitlePopupOpen, setIssueTitlePopupOpen] = useState(false);
  const [descriptionPopupOpen, setDescriptionPopupOpen] = useState(false);
  const [editedDueDate, setEditedDueDate] = useState('');
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [availableIssueTypes, setAvailableIssueTypes] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  const [issueColors, setIssueColors] = useState({});
  const [initialRanks, setInitialRanks] = useState({});
  const [preferencesLoading, setPreferencesLoading] = useState(true);
  const [recentIssueKeys, setRecentIssueKeys] = useState([]); // Array of issue keys in recent order
  const [issueKeyFromHash, setIssueKeyFromHash] = useState(null); // Issue key to restore from URL hash
  const [isRestoringFromHash, setIsRestoringFromHash] = useState(true); // Prevent hash updates during initial restore
  const [transitionIssue, setTransitionIssue] = useState(null);
  const [availableTransitions, setAvailableTransitions] = useState([]);
  const [loadingTransitions, setLoadingTransitions] = useState(false);
  
  // Drag and drop state
  const [draggedIssue, setDraggedIssue] = useState(null);
  const [dragOverIssue, setDragOverIssue] = useState(null);
  const [dropPosition, setDropPosition] = useState('after'); // 'before' or 'after'
  const [isDraggingSort, setIsDraggingSort] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [droppedIssueKey, setDroppedIssueKey] = useState(null);
  const longPressTimerRef = useRef(null);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const autoScrollIntervalRef = useRef(null);
  const lastValidDropTarget = useRef({ issueKey: null, position: 'after', prevIssue: null, nextIssue: null });
  
  // Swipe state for each row
  const [swipeStates, setSwipeStates] = useState({});
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const swipeStartX = useRef(0); // Track initial swipe position for easier centering
  const isDragging = useRef(false);
  const currentSwipeKey = useRef(null);
  const currentSwipeX = useRef(0);  // Track current position for desktop
  const swipeThreshold = 60;   // Threshold to snap open
  const startThreshold = 10;   // Minimum movement to start swipe
  const containerRef = useRef(null);
  
  // Helper to get authorization header
  const getAuthHeader = () => {
    if (user.useSession && user.sessionToken) {
      return `Session ${user.sessionToken}`;
    }
    return `Bearer ${user.idToken}`;
  };

  // Memoize auth token to prevent unnecessary re-renders
  const authToken = useMemo(() => getAuthHeader(), [user.useSession, user.sessionToken, user.idToken]);

  // Use fast issues cache for ALL issues
  const { issues: allIssues, loading, refresh } = useIssueFeed('all', authToken);

  const saveIssueFields = async (fields, closePopup) => {
    if (!selectedIssue?.key || issueSaving) return;
    setIssueSaving(true);
    try {
      const { issue } = await updateIssue(selectedIssue.key, fields, getAuthHeader());
      issueCacheSingleton.get('all').upsertAndNotify(issue);
      setSelectedIssue(issue);
      closePopup();
    } catch (error) {
      console.error('[JiraDashboard] Issue update failed:', error);
      setIssueError(error.message || 'Unable to save the issue change.');
    } finally {
      setIssueSaving(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchIssueTypes(authToken)
      .then(({ issueTypes }) => {
        if (!cancelled) setAvailableIssueTypes(issueTypes || []);
      })
      .catch((error) => console.warn('[JiraDashboard] Unable to load issue types:', error.message));
    return () => { cancelled = true; };
  }, [authToken]);

  useEffect(() => {
    let cancelled = false;
    fetchAssignableUsers(authToken)
      .then(({ users }) => { if (!cancelled) setAvailableUsers(users || []); })
      .catch((error) => console.warn('[JiraDashboard] Unable to load Jira users:', error.message));
    return () => { cancelled = true; };
  }, [authToken]);

  // Filter issues by scope and search
  const issues = useMemo(() => {
    const filterStart = performance.now();

    console.log('[JiraDashboard] Filtering issues:', {
      totalIssues: allIssues.length,
      viewMode,
      loading,
      sampleIssue: allIssues[0],
      sampleAssignee: allIssues[0]?.assignee,
      hasAvatarUrl: !!allIssues[0]?.assignee?.avatarUrl
    });

    let scoped = allIssues;

    // Apply scope filter using backend-provided issueScope field
    if (viewMode === VIEW_MODES.MY) {
      scoped = allIssues.filter(issue => issue.issueScope === 'my');
    } else if (viewMode === VIEW_MODES.OTHERS) {
      scoped = allIssues.filter(issue => issue.issueScope === 'others');
    } else if (viewMode === VIEW_MODES.RECENT) {
      // Filter by recent issue keys and preserve order
      const recentSet = new Set(recentIssueKeys);
      console.log('[JiraDashboard] Recent mode - recentIssueKeys:', recentIssueKeys.length, recentIssueKeys.slice(0, 5));
      scoped = allIssues.filter(issue => recentSet.has(issue.key));
      console.log('[JiraDashboard] Recent mode - filtered issues:', scoped.length);
      // Sort by recent order
      scoped.sort((a, b) => recentIssueKeys.indexOf(a.key) - recentIssueKeys.indexOf(b.key));
    }
    // For BOARD mode, we'll handle it differently later

    console.log('[JiraDashboard] After scope filter:', { scoped: scoped.length });

    // Apply smart search filter if active
    if (smartSearchIds !== null) {
      const idSet = new Set(smartSearchIds);
      scoped = scoped.filter(issue => idSet.has(issue.id));
    } else {
      // Apply regular text search
      scoped = searchIssues(scoped, searchQuery);
    }

    console.log('[JiraDashboard] After search filter:', { final: scoped.length });

    // Apply dropdown filters (multi-select)
    if (filters.status.length > 0) {
      scoped = scoped.filter(issue => filters.status.includes(issue.fields?.status?.name));
    }
    if (filters.assignee.length > 0) {
      scoped = scoped.filter(issue => {
        const assignee = issue.assignee?.display || issue.fields?.assignee?.displayName || 'Unassigned';
        return filters.assignee.includes(assignee);
      });
    }
    if (filters.priority.length > 0) {
      scoped = scoped.filter(issue => filters.priority.includes(issue.fields?.priority?.name));
    }
    if (filters.type.length > 0) {
      scoped = scoped.filter(issue => filters.type.includes(issue.fields?.issuetype?.name));
    }

    const filterDuration = performance.now() - filterStart;
    if (filterDuration > 10) {
      console.log('[JiraDashboard] Issues filter memo:', { duration: `${filterDuration.toFixed(0)}ms`, resultCount: scoped.length });
    }

    return scoped;
  }, [allIssues, viewMode, searchQuery, smartSearchIds, loading, filters, recentIssueKeys]);

  // Extract unique values for filters
  const filterOptions = useMemo(() => {
    const statuses = new Set();
    const assignees = new Set();
    const priorities = new Set();
    const types = new Set();

    allIssues.forEach(issue => {
      if (issue.fields?.status?.name) statuses.add(issue.fields.status.name);
      const assignee = issue.assignee?.display || issue.fields?.assignee?.displayName || 'Unassigned';
      assignees.add(assignee);
      if (issue.fields?.priority?.name) priorities.add(issue.fields.priority.name);
      if (issue.fields?.issuetype?.name) types.add(issue.fields.issuetype.name);
    });

    return {
      statuses: Array.from(statuses).sort(),
      assignees: Array.from(assignees).sort(),
      priorities: Array.from(priorities).sort(),
      types: Array.from(types).sort()
    };
  }, [allIssues]);

  // Fetch user preferences
  useEffect(() => {
    fetchUserPreferences();
  }, [viewMode]);

  // Load recent issues on mount
  useEffect(() => {
    loadRecentIssues();
  }, []);

  // Update URL hash when state changes (but not during initial restore)
  useEffect(() => {
    // Don't update hash while restoring from initial URL
    if (isRestoringFromHash) return;

    const params = new URLSearchParams();

    // Add filters to params
    if (filters.status.length > 0) params.set('status', filters.status.join(','));
    if (filters.assignee.length > 0) params.set('assignee', filters.assignee.join(','));
    if (filters.priority.length > 0) params.set('priority', filters.priority.join(','));
    if (filters.type.length > 0) params.set('type', filters.type.join(','));
    if (searchQuery) params.set('q', searchQuery);

    // Build hash with view mode and optional issue
    let hash = `#${viewMode.toLowerCase()}`;
    if (selectedIssue?.key) {
      hash += `/${selectedIssue.key}`;
    }

    const queryString = params.toString();
    if (queryString) {
      hash += `?${queryString}`;
    }

    // Update hash without triggering reload
    if (window.location.hash !== hash) {
      window.history.replaceState(null, '', hash);
    }
  }, [viewMode, filters, searchQuery, selectedIssue, isRestoringFromHash]);

  // Parse hash and extract issue key and view mode
  const parseHash = useCallback(() => {
    const hash = window.location.hash.slice(1); // Remove #
    if (!hash) return null;

    // Parse hash format: viewMode/issueKey?params
    const [pathPart, queryPart] = hash.split('?');
    const [viewPart, issueKey] = pathPart.split('/');

    return { viewPart, issueKey, queryPart };
  }, []);

  // Restore state from URL hash on mount
  useEffect(() => {
    const restoreFromHash = () => {
      const parsed = parseHash();
      if (!parsed) return;

      const { viewPart, issueKey, queryPart } = parsed;

      // Restore view mode
      const viewModeMap = {
        'my': VIEW_MODES.MY,
        'others': VIEW_MODES.OTHERS,
        'recent': VIEW_MODES.RECENT,
        'board': VIEW_MODES.BOARD
      };
      if (viewModeMap[viewPart]) {
        setViewMode(viewModeMap[viewPart]);
      }

      // Restore filters from query params
      if (queryPart) {
        const params = new URLSearchParams(queryPart);
        const newFilters = { status: [], assignee: [], priority: [], type: [] };

        if (params.has('status')) newFilters.status = params.get('status').split(',');
        if (params.has('assignee')) newFilters.assignee = params.get('assignee').split(',');
        if (params.has('priority')) newFilters.priority = params.get('priority').split(',');
        if (params.has('type')) newFilters.type = params.get('type').split(',');

        setFilters(newFilters);

        if (params.has('q')) setSearchQuery(params.get('q'));
      }

      // Restore selected issue (will be opened after issues load)
      if (issueKey) {
        setIssueKeyFromHash(issueKey);
      }
    };

    restoreFromHash();

    // Also listen for hash changes while mounted (e.g., from ChatWindow context menu)
    const handleHashChange = () => {
      const parsed = parseHash();
      if (parsed?.issueKey) {
        // Navigate to the new issue immediately - don't check if same as current
        // navigateToIssue will fetch fresh data regardless
        setIssueKeyFromHash(parsed.issueKey);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only set up on mount

  // Track if hash issue should be opened (deferred to after navigateToIssue is defined)
  const [pendingHashIssue, setPendingHashIssue] = useState(null);

  // Open selected issue from hash IMMEDIATELY - don't wait for issue list to load
  useEffect(() => {
    if (issueKeyFromHash && !selectedIssue) {
      // Immediately set pending issue - navigateToIssue will show loading state and fetch from API
      // Try to find in cache if available, otherwise pass null
      const cachedIssue = !loading ? allIssues.find(i => i.key === issueKeyFromHash) : null;
      setPendingHashIssue({ key: issueKeyFromHash, cached: cachedIssue || null });
      // Clear issueKeyFromHash so we don't keep reopening
      setIssueKeyFromHash(null);
      // Restore complete - allow hash updates
      setIsRestoringFromHash(false);
    } else if (!issueKeyFromHash && !selectedIssue) {
      // No issue to restore - allow hash updates (but only if we've had a chance to check)
      setIsRestoringFromHash(false);
    }
  }, [issueKeyFromHash, selectedIssue, loading, allIssues]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdown && !e.target.closest('.custom-dropdown')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);
  
  // Debug color changes
  useEffect(() => {
    // console.log('issueColors state updated:', issueColors);
  }, [issueColors]);
  
  // Save ranks function for the new ordering system
  const saveRanks = async (updates) => {
    try {
      const response = await fetch('/api/jira/sort', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({
          view: viewMode.toLowerCase(),
          updates
        })
      });
      if (!response.ok) {
        console.error('[JiraDashboard] Failed to save ranks:', response.status);
      } else {
        console.log('[JiraDashboard] Ranks saved successfully');
      }
    } catch (err) {
      console.error('Failed to save ranks:', err);
    }
  };

  // Group function for separating sections
  // MUST be memoized to prevent constant re-renders and re-saves
  // Only separate by status (active/done), NOT by pinned/unpinned
  // Pinned items are rendered first within each section
  const groupBy = useCallback((issue) => {
    const status = issue.fields?.status?.name?.toLowerCase() || '';
    const isDone = status.includes('done') ||
                   status.includes('on hold') ||
                   status.includes('closed');

    return isDone ? 'done' : 'active';
  }, []);

  // Use the ordering hook (skip for RECENT mode - use natural order)
  const { orderedIssues, moveIssue } = useIssueOrdering({
    viewKey: viewMode.toLowerCase(),
    issues: viewMode === VIEW_MODES.RECENT ? [] : issues, // Skip ordering for RECENT
    initialRanks,
    pinnedSet: new Set(pinnedIssues),
    groupBy,
    saveRanks,
  });

  // For RECENT mode, use issues as-is (already sorted by recent access)
  const finalOrderedIssues = viewMode === VIEW_MODES.RECENT ? issues : orderedIssues;

  
  
  // Add global mouse and touch event listeners for drag and drop
  useEffect(() => {
    if (!isDraggingSort) return;
    
    const handleGlobalMove = (e) => {
      handleLongPressMove(e);
    };
    
    const handleGlobalEnd = (e) => {
      handleLongPressEnd(e);
    };
    
    // Add both mouse and touch listeners
    document.addEventListener('mousemove', handleGlobalMove);
    document.addEventListener('mouseup', handleGlobalEnd);
    document.addEventListener('touchmove', handleGlobalMove, { passive: false });
    document.addEventListener('touchend', handleGlobalEnd);
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('mouseup', handleGlobalEnd);
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalEnd);
    };
  }, [isDraggingSort]);
  
  // Close swipes on scroll
  const handleScroll = () => {
    // Close all open swipes when scrolling
    const hasOpenSwipes = Object.values(swipeStates).some(state => state && state.x !== 0);
    if (hasOpenSwipes) {
      setSwipeStates(prev => {
        const newStates = {};
        Object.keys(prev).forEach(key => {
          newStates[key] = { x: 0, settled: true };
        });
        return newStates;
      });
    }
  };

  // Handle smart search
  const handleSmartSearch = async () => {
    if (!searchQuery.trim()) return;

    setSmartSearchLoading(true);
    try {
      const response = await fetch('/api/issues/smartSearch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify({ searchText: searchQuery })
      });

      if (!response.ok) throw new Error('Smart search failed');

      const data = await response.json();
      setSmartSearchIds(data.issueIds);
      console.log('[JiraDashboard] Smart search found:', data.issueIds.length, 'issues');
    } catch (error) {
      console.error('[JiraDashboard] Smart search error:', error);
      alert('Smart search failed. Please try again.');
    } finally {
      setSmartSearchLoading(false);
    }
  };

  const fetchUserPreferences = async () => {
    setPreferencesLoading(true);
    try {
      const params = new URLSearchParams({ view: viewMode.toLowerCase() });
      const response = await fetch(`/api/jira/preferences?${params}`, {
        headers: {
          Authorization: getAuthHeader()
        }
      });

      if (response.ok) {
        const prefs = await response.json();
        console.log('[JiraDashboard] Fetched preferences for view:', viewMode, prefs);
        setPinnedIssues(prefs.pinnedIssues || []);
        setIssueColors(prefs.colors || {});
        setInitialRanks(prefs.ranks || {});
      } else {
        // Even on error, set empty preferences so we can show issues
        console.warn('[JiraDashboard] Failed to fetch preferences, using defaults');
        setPinnedIssues([]);
        setIssueColors({});
        setInitialRanks({});
      }
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
      // Set empty preferences so we can show issues
      setPinnedIssues([]);
      setIssueColors({});
      setInitialRanks({});
    } finally {
      setPreferencesLoading(false);
    }
  };

  // Load recent issues on mount
  const loadRecentIssues = async () => {
    console.log('[JiraDashboard] Loading recent issues...');
    try {
      const response = await fetch('/api/jira/user/recent-issues', {
        headers: {
          Authorization: getAuthHeader()
        }
      });

      console.log('[JiraDashboard] Load recent response:', response.status, response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('[JiraDashboard] Recent issues data:', data);
        const keys = (data.recentIssues || []).map(item => item.issueKey);
        console.log('[JiraDashboard] Loaded recent issues, count:', keys.length, 'keys:', keys.slice(0, 5));
        setRecentIssueKeys(keys);
      } else {
        const errorText = await response.text();
        console.error('[JiraDashboard] Failed to load recent:', response.status, errorText);
      }
    } catch (err) {
      console.error('Failed to load recent issues:', err);
    }
  };

  // Mark issue as recently accessed
  const markIssueAsRecent = async (issueKey) => {
    console.log('[JiraDashboard] Marking issue as recent:', issueKey);
    try {
      const response = await fetch('/api/jira/user/recent-issues', {
        method: 'POST',
        headers: {
          'Authorization': getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ issueKey })
      });

      console.log('[JiraDashboard] Mark recent response:', response.status, response.ok);

      if (response.ok) {
        const data = await response.json();
        const keys = (data.recentIssues || []).map(item => item.issueKey);
        console.log('[JiraDashboard] Updated recent issues, count:', keys.length, 'keys:', keys.slice(0, 5));
        setRecentIssueKeys(keys);
      } else {
        const errorText = await response.text();
        console.error('[JiraDashboard] Failed to mark recent:', response.status, errorText);
      }
    } catch (err) {
      console.error('Failed to mark issue as recent:', err);
    }
  };

  /**
   * Navigate to an issue - the central function for all issue navigation
   * Shows loading state immediately, fetches fresh data with access check
   * @param {string} issueKey - Issue key (e.g., 'PROJ-123')
   * @param {Object} [cachedIssue] - Optional cached issue to show immediately
   */
  const navigateToIssue = useCallback(async (issueKey, cachedIssue = null) => {
    if (!issueKey) return;

    console.log('[JiraDashboard] navigateToIssue:', issueKey, cachedIssue ? '(with cache)' : '(no cache)');

    // Clear any previous error
    setIssueError(null);
    setIssueLoading(true);

    // Reset comments
    setIssueComments([]);
    setCommentsStartAt(0);
    setCommentsTotal(0);

    // If we have cached data, show it immediately
    if (cachedIssue) {
      setSelectedIssue(cachedIssue);
    } else {
      // Check if issue is in cache
      const cached = issueCacheSingleton.get('all').getByKey(issueKey);
      if (cached) {
        setSelectedIssue(cached);
      } else {
        // Show placeholder with just the key
        setSelectedIssue({ key: issueKey, fields: null, _loading: true });
      }
    }

    // Mark as recent
    markIssueAsRecent(issueKey);

    // Fetch fresh data from API (with access check)
    try {
      const response = await fetchIssue(issueKey, authToken);
      const freshIssue = response.issue;

      // Update cache
      issueCacheSingleton.get('all').upsertAndNotify(freshIssue);

      // Update selected issue with fresh data
      setSelectedIssue(freshIssue);
      setIssueLoading(false);
      setIssueError(null);

    } catch (err) {
      console.error('[JiraDashboard] Failed to fetch issue:', err);
      setIssueLoading(false);

      // Show error - 404 or no access
      if (err.status === 404 || err.code === 'not_found') {
        setIssueError('Issue not found or you do not have access');
        setSelectedIssue({ key: issueKey, fields: null, _error: true });
      } else {
        setIssueError(err.message || 'Failed to load issue');
        // Keep the cached data if we had any
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  // Handle pending hash issue (after navigateToIssue is defined)
  useEffect(() => {
    if (pendingHashIssue) {
      navigateToIssue(pendingHashIssue.key, pendingHashIssue.cached);
      setPendingHashIssue(null);
    }
  }, [pendingHashIssue, navigateToIssue]);

  // Unified handlers for both touch and mouse
  const handleSwipeStart = (e, issueKey) => {
    // Don't start swipe if we're in drag sort mode or already have a long press timer
    if (isDraggingSort || longPressTimerRef.current) return;
    
    const isTouch = e.type.includes('touch');
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    
    touchStartX.current = clientX;
    touchStartY.current = clientY;
    isDragging.current = false;
    currentSwipeKey.current = issueKey;
    
    // Track the starting swipe position (where the item was when we started)
    swipeStartX.current = swipeStates[issueKey]?.x || 0;
    
    // Initialize swipe state if needed, ensuring dragging is false
    setSwipeStates(prev => ({
      ...prev,
      [issueKey]: prev[issueKey] || { x: 0, settled: true, dragging: false }
    }));
    
    // For mouse events, add global listeners
    if (!isTouch) {
      const handleGlobalMouseMove = (e) => {
        // Use the ref to get current key since it might change
        if (currentSwipeKey.current === issueKey) {
          handleSwipeMove(e, issueKey);
        }
      };
      const handleGlobalMouseUp = (e) => {
        // Use the ref to get current key for the end
        if (currentSwipeKey.current === issueKey) {
          handleSwipeEnd(e, issueKey);
        }
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
      
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }
  };
  
  const handleSwipeMove = (e, issueKey) => {
    // Don't process swipe if we're in drag sort mode
    if (isDraggingSort) return;
    if (!touchStartX.current || currentSwipeKey.current !== issueKey) return;
    
    const isTouch = e.type.includes('touch');
    const currentX = isTouch ? e.touches[0].clientX : e.clientX;
    const currentY = isTouch ? e.touches[0].clientY : e.clientY;
    const deltaX = currentX - touchStartX.current;
    const deltaY = Math.abs(currentY - touchStartY.current);
    
    // Determine if we're swiping horizontally
    if (!isDragging.current) {
      // Need minimum movement to start
      if (Math.abs(deltaX) > startThreshold || deltaY > startThreshold) {
        // If vertical scroll, cancel swipe and allow normal scrolling
        if (deltaY > Math.abs(deltaX)) {
          touchStartX.current = null;
          currentSwipeKey.current = null;
          return;
        }
        // Horizontal swipe detected
        isDragging.current = true;
        hasMoved.current = true;
        
        // Mark as dragging and close other swipes
        setSwipeStates(prev => {
          const newState = { ...prev };
          // Close all other swipes
          Object.keys(newState).forEach(key => {
            if (key !== issueKey && newState[key]?.x !== 0) {
              newState[key] = { x: 0, settled: true, dragging: false };
            }
          });
          // Set current item as dragging
          newState[issueKey] = { x: 0, settled: false, dragging: true };
          return newState;
        });
      } else {
        return;
      }
    }
    
    // While dragging, update position
    if (isDragging.current && currentSwipeKey.current === issueKey) {
      // Only prevent default for touch events during horizontal swipe
      if (isTouch) {
        e.preventDefault();
      }
      
      // Direct translation
      let translateX = deltaX;
      const maxSwipe = 180;
      
      // Apply rubber band at edges
      if (Math.abs(translateX) > maxSwipe) {
        const overflow = Math.abs(translateX) - maxSwipe;
        const resistance = 1 - (overflow / (overflow + 100));
        translateX = Math.sign(translateX) * (maxSwipe + overflow * resistance * 0.3);
      }
      
      // Update only this specific issue's position
      setSwipeStates(prev => ({
        ...prev,
        [issueKey]: { x: translateX, settled: false, dragging: true }
      }));
      
      // Also track in ref for desktop
      currentSwipeX.current = translateX;
    }
  };
  
  const handleSwipeEnd = (e, issueKey) => {
    // Don't process swipe if we're in drag sort mode
    if (isDraggingSort) {
      touchStartX.current = null;
      currentSwipeKey.current = null;
      currentSwipeX.current = 0;
      return;
    }
    
    if (!isDragging.current || currentSwipeKey.current !== issueKey) {
      touchStartX.current = null;
      currentSwipeKey.current = null;
      currentSwipeX.current = 0;
      return;
    }
    
    // For desktop, use the ref value; for touch, use state
    const isTouch = e.type.includes('touch');
    const currentX = isTouch ? (swipeStates[issueKey]?.x || 0) : currentSwipeX.current;
    
    // Check if item was already swiped before this interaction
    const wasAlreadySwiped = Math.abs(swipeStartX.current) > 100; // Was at 160 when we started
    
    // console.log('handleSwipeEnd:', { issueKey, currentX, wasAlreadySwiped, startX: swipeStartX.current });
    
    // Determine final position
    let finalX = 0;
    
    if (wasAlreadySwiped) {
      // Item was already swiped open
      const startDirection = swipeStartX.current > 0 ? 'right' : 'left';
      const movedTowardCenter = Math.abs(currentX) < Math.abs(swipeStartX.current) - 10; // Moved at least 10px toward center
      
      if (movedTowardCenter) {
        // User is trying to close it - snap to center
        finalX = 0;
      } else if (Math.abs(currentX) > 140) {
        // Still far enough to stay open or switch sides
        if (currentX > 0) {
          finalX = 160;
        } else {
          finalX = -160;
        }
      } else {
        // In the middle zone - return to original position
        finalX = swipeStartX.current > 0 ? 160 : -160;
      }
    } else {
      // Item was centered - use normal threshold
      if (Math.abs(currentX) > swipeThreshold) {
        if (currentX > 0) {
          finalX = 160;
        } else {
          finalX = -160;
        }
      }
    }
    
    // console.log('Final position:', finalX);
    
    // Update state with final position
    setSwipeStates(prev => ({
      ...prev,
      [issueKey]: { x: finalX, settled: true, dragging: false }
    }));
    
    // Clear refs
    touchStartX.current = null;
    isDragging.current = false;
    currentSwipeKey.current = null;
    currentSwipeX.current = 0;
  };
  
  // Track if we actually moved during drag
  const hasMoved = useRef(false);
  
  // Handle long press for drag and drop
  const handleLongPressStart = (e, issue) => {
    // console.log('Long press start', { isDragging: isDragging.current, isDraggingSort, issue: issue.key });

    // Prevent default browser drag behavior
    e.preventDefault();

    // Don't start another drag if we're already dragging or swiping
    if (isDragging.current || isDraggingSort) {
      // console.log('Skipping long press - already dragging');
      return;
    }

    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    
    dragStartPosRef.current = { x: clientX, y: clientY };
    
    // Start long press timer
    longPressTimerRef.current = setTimeout(() => {
      // console.log('Long press activated!', issue.key);
      
      // Vibrate if available (for mobile)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      setIsDraggingSort(true);
      setDraggedIssue(issue);
      lastValidDropTarget.current = { issueKey: null, position: 'after', prevIssue: null, nextIssue: null };
      setDragPosition({ x: clientX, y: clientY });
      
      // Cancel any swipe in progress
      isDragging.current = false;
      currentSwipeKey.current = null;
      currentSwipeX.current = 0;
      
      // Close any open swipe menus
      setSwipeStates({});
      
      // Prevent scroll on mobile when drag starts
      if (isTouch && e.cancelable) {
        e.preventDefault();
      }
    }, 300); // 300ms long press for faster activation
  };
  
  const handleLongPressMove = (e) => {
    if (!longPressTimerRef.current && !isDraggingSort) return;
    
    const isTouch = e.type === 'touchmove';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    
    // Cancel on both horizontal and vertical movement
    if (longPressTimerRef.current) {
      const dx = Math.abs(clientX - dragStartPosRef.current.x);
      const dy = Math.abs(clientY - dragStartPosRef.current.y);
      
      // Check both horizontal and vertical movement for cancellation
      if (dx > 15 || dy > 15) {
        // console.log('Cancelling long press due to movement', { dx, dy });
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
        // Also clear auto-scroll if it was started
        if (autoScrollIntervalRef.current) {
          clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }
      }
    }
    
    // If dragging for sort, prevent default scrolling on touch devices
    if (isDraggingSort && draggedIssue) {
      // Prevent scrolling while dragging
      if (isTouch && e.cancelable) {
        e.preventDefault();
      }
      // Update drag position
      setDragPosition({ x: clientX, y: clientY });
      
      // Auto-scroll when near edges
      const scrollThreshold = 100; // pixels from edge to start scrolling
      const scrollSpeed = 10; // pixels per frame
      const viewportHeight = window.innerHeight;
      
      // Clear any existing auto-scroll
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }
      
      // Check if near top or bottom
      if (clientY < scrollThreshold) {
        // Near top - scroll up
        const intensity = 1 - (clientY / scrollThreshold);
        autoScrollIntervalRef.current = setInterval(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop -= scrollSpeed * intensity;
          }
        }, 16); // ~60fps
      } else if (clientY > viewportHeight - scrollThreshold) {
        // Near bottom - scroll down
        const intensity = 1 - ((viewportHeight - clientY) / scrollThreshold);
        autoScrollIntervalRef.current = setInterval(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop += scrollSpeed * intensity;
          }
        }, 16); // ~60fps
      }
      
      // Determine which section the dragged issue is in
      const draggedStatus = draggedIssue.fields?.status?.name?.toLowerCase() || '';
      const isDraggedDone = draggedStatus.includes('done') || 
                            draggedStatus.includes('on hold') || 
                            draggedStatus.includes('onhold') || 
                            draggedStatus.includes('closed') || 
                            draggedStatus.includes('resolved') ||
                            draggedStatus.includes('complete');
      
      // Find element under cursor/touch
      const elementBelow = document.elementFromPoint(clientX, clientY);
      const issueRow = elementBelow?.closest('.jira-issue-wrapper');
      
      if (issueRow) {
        const overIssueKey = issueRow.dataset.issueKey;
        
        // Skip if it's the dragged item itself (shouldn't happen but just in case)
        if (overIssueKey === draggedIssue.key) {
          return;
        }
        // Find the target issue to check its section
        const overIssue = issues.find(i => i.key === overIssueKey);
        if (overIssue) {
          const overStatus = overIssue.fields?.status?.name?.toLowerCase() || '';
          const isOverDone = overStatus.includes('done') || 
                            overStatus.includes('on hold') || 
                            overStatus.includes('onhold') || 
                            overStatus.includes('closed') || 
                            overStatus.includes('resolved') ||
                            overStatus.includes('complete');
          
          // Only allow drop if in the same section
          if (isDraggedDone === isOverDone) {
            // Determine if we're in the top or bottom half of the element
            const rect = issueRow.getBoundingClientRect();
            const midpoint = rect.top + rect.height / 2;
            
            // Get the current section's issues to find prev/next
            const sectionIssues = isDraggedDone ? sortedIssues.done : sortedIssues.active;

            // Check if we're dragging within the same pin group or across groups
            const isDraggedPinned = pinnedIssues.includes(draggedIssue.key);
            const isOverPinned = pinnedIssues.includes(overIssueKey);
            const isSamePinGroup = isDraggedPinned === isOverPinned;

            // If dragging within same pin group, filter to only that group for accurate neighbor calculation
            // If dragging across groups (for auto-pin/unpin), use full section
            const relevantIssues = isSamePinGroup
              ? sectionIssues.filter(i =>
                  i.key !== draggedIssue.key &&
                  (pinnedIssues.includes(i.key) === isDraggedPinned)
                )
              : sectionIssues.filter(i => i.key !== draggedIssue.key);

            const targetIdx = relevantIssues.findIndex(i => i.key === overIssueKey);

            if (clientY < midpoint) {
              // Dropping before this item
              setDropPosition('before');
              const prevIssue = targetIdx > 0 ? relevantIssues[targetIdx - 1].key : null;
              const nextIssue = relevantIssues[targetIdx].key;
              lastValidDropTarget.current = {
                issueKey: overIssueKey,
                position: 'before',
                prevIssue: prevIssue,
                nextIssue: nextIssue
              };
            } else {
              // Dropping after this item
              setDropPosition('after');
              const prevIssue = relevantIssues[targetIdx].key;
              const nextIssue = targetIdx < relevantIssues.length - 1 ? relevantIssues[targetIdx + 1].key : null;
              lastValidDropTarget.current = {
                issueKey: overIssueKey,
                position: 'after',
                prevIssue: prevIssue,
                nextIssue: nextIssue
              };
            }
            setDragOverIssue(overIssueKey);
          } else {
            setDragOverIssue(null);
          }
        }
      } else {
        // Check if we're above the first issue in the same section
        const sectionClass = isDraggedDone ? '.done-section' : '.active-section';
        const sectionElement = document.querySelector(sectionClass);
        if (sectionElement) {
          const firstWrapper = sectionElement.querySelector('.jira-issue-wrapper');
          if (firstWrapper) {
            const rect = firstWrapper.getBoundingClientRect();
            if (clientY < rect.top) {
              setDragOverIssue('__first__'); // Special marker for first position
              const sectionIssues = isDraggedDone ? sortedIssues.done : sortedIssues.active;

              // For first position, always use first item of the section as next
              // (This allows dropping unpinned at first position to pin it)
              const filteredSection = sectionIssues.filter(i => i.key !== draggedIssue.key);

              lastValidDropTarget.current = {
                issueKey: '__first__',
                position: 'before',
                prevIssue: null,
                nextIssue: filteredSection.length > 0 ? filteredSection[0].key : null
              };
            } else {
              setDragOverIssue(null);
            }
          }
        } else {
          setDragOverIssue(null);
        }
      }
    }
  };
  
  const handleLongPressEnd = async (e) => {
    // console.log('Long press end', { isDraggingSort, draggedIssue: draggedIssue?.key, dragOverIssue, lastValidDropTarget: lastValidDropTarget.current });
    
    // Clear long press timer if still running
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    
    // Handle drop if we were dragging
    if (isDraggingSort && draggedIssue) {
      // Use last valid drop target if we have one
      const dropTarget = lastValidDropTarget.current.issueKey || dragOverIssue;
      const dropPos = lastValidDropTarget.current.position || dropPosition;
      const prevIssue = lastValidDropTarget.current.prevIssue;
      const nextIssue = lastValidDropTarget.current.nextIssue;
      
      // console.log('Dropping', draggedIssue.key, 'between', prevIssue, 'and', nextIssue);
      
      // Prevent dropping on itself
      if (dropTarget === draggedIssue.key) {
        // console.log('Dropping on itself - ignored');
        setIsDraggingSort(false);
        setDraggedIssue(null);
        setDragOverIssue(null);
        setDropPosition('after');
        lastValidDropTarget.current = { issueKey: null, position: 'after' };
        return;
      }
      
      // Only proceed if we have a valid drop target
      // Note: prevIssue can be null (for first position), undefined means no drop occurred
      if (dropTarget && (prevIssue !== undefined || nextIssue !== undefined)) {
        // Use the new moveIssue function from the hook to handle all the sorting logic
        // Don't await - let it update optimistically
        moveIssue(draggedIssue.key, prevIssue, nextIssue);
        
        // Check if we need to update pin status based on drop position
        const isDraggedPinned = pinnedIssues.includes(draggedIssue.key);
        const isPrevPinned = prevIssue ? pinnedIssues.includes(prevIssue) : false;
        const isNextPinned = nextIssue ? pinnedIssues.includes(nextIssue) : false;
        
        // Determine if item should be pinned or unpinned based on neighbors
        let newPinnedIssues = [...pinnedIssues];
        let pinStatusChanged = false;
        
        if (!isDraggedPinned && (isPrevPinned || (prevIssue === null && isNextPinned))) {
          // Auto-pin: dropping unpinned item before/between pinned items
          // console.log('Auto-pinning item dropped in pinned section');
          
          // Find insert position in pinned array
          let insertIndex = 0;
          if (prevIssue && isPrevPinned) {
            insertIndex = newPinnedIssues.indexOf(prevIssue) + 1;
          } else if (prevIssue === null && isNextPinned) {
            insertIndex = 0; // First position
          }
          
          newPinnedIssues.splice(insertIndex, 0, draggedIssue.key);
          pinStatusChanged = true;
          
        } else if (isDraggedPinned && prevIssue && !isPrevPinned) {
          // Auto-unpin: dropping pinned item after unpinned item
          // console.log('Auto-unpinning item dropped in unpinned section');
          newPinnedIssues = newPinnedIssues.filter(k => k !== draggedIssue.key);
          pinStatusChanged = true;
          
        } else if (isDraggedPinned && (isPrevPinned || prevIssue === null)) {
          // Reorder within pinned section
          // console.log('Reordering within pinned section');
          
          // Remove from old position
          newPinnedIssues = newPinnedIssues.filter(k => k !== draggedIssue.key);
          
          // Find new position
          let insertIndex = 0;
          if (prevIssue && isPrevPinned) {
            const prevIndex = newPinnedIssues.indexOf(prevIssue);
            insertIndex = prevIndex + 1;
          }
          
          newPinnedIssues.splice(insertIndex, 0, draggedIssue.key);
          
          // Check if order actually changed
          const oldIndex = pinnedIssues.indexOf(draggedIssue.key);
          const newIndex = newPinnedIssues.indexOf(draggedIssue.key);
          pinStatusChanged = oldIndex !== newIndex;
        }
        
        // Update pin status if changed
        if (pinStatusChanged) {
          setPinnedIssues(newPinnedIssues);

          // Save to backend asynchronously - don't block UI
          setTimeout(() => {
            fetch('/api/jira/pin', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: getAuthHeader()
              },
              body: JSON.stringify({
                issueKey: draggedIssue.key,
                pinned: newPinnedIssues.includes(draggedIssue.key),
                pinnedOrder: newPinnedIssues
              })
            }).catch(err => console.error('Failed to update pin status:', err));
          }, 0);
        }
        
        // Set dropped item for animation
        setDroppedIssueKey(draggedIssue.key);
        setTimeout(() => setDroppedIssueKey(null), 500); // Clear after animation
      } // end of if (dropTarget)
    } // end of if (isDraggingSort && draggedIssue)
    
    // Reset drag state
    setIsDraggingSort(false);
    setDraggedIssue(null);
    setDragOverIssue(null);
    setDropPosition('after');
    setDragPosition({ x: 0, y: 0 });
    lastValidDropTarget.current = { issueKey: null, position: 'after', prevIssue: null, nextIssue: null };
    
    // Clear auto-scroll interval
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };
  
  // Handle click to close swipe
  const handleRowClick = (e, issue) => {
    // Prevent event bubbling
    e.stopPropagation();
    
    // Don't open if we just finished dragging
    if (hasMoved.current) {
      hasMoved.current = false;
      return;
    }
    
    // Don't open if we were sorting
    if (isDraggingSort) {
      return;
    }
    
    if (!issue || !issue.key) {
      console.error('Invalid issue object:', issue);
      return;
    }
    
    const state = swipeStates[issue.key];
    
    // If swiped open, close it first
    if (state && Math.abs(state.x) > 0) {
      setSwipeStates(prev => ({
        ...prev,
        [issue.key]: { x: 0, settled: true }
      }));
      return;
    }

    // Use navigateToIssue for unified navigation with loading states
    // Pass cached issue data if we have it, or just the key
    navigateToIssue(issue.key, issue.fields ? issue : null);
  };
  
  // Fetch comments for an issue
  // Helper function to convert Atlassian Document Format to HTML
  const convertADFToHTML = (content) => {
    if (typeof content === 'string') {
      // Even plain-string descriptions need escaping — Jira can return raw
      // text here, and we're about to pump it into innerHTML.
      return esc(content);
    }

    if (!content || !content.content) {
      return '';
    }

    const renderNode = (node) => {
      let html = '';

      // Handle text nodes with marks
      if (node.type === 'text') {
        // Escape the raw text first. From here on we're building HTML around
        // already-safe text. SECURITY_AUDIT C-1.
        let text = esc(node.text || '');

        // Apply marks (bold, italic, code, etc.)
        if (node.marks && node.marks.length > 0) {
          node.marks.forEach(mark => {
            switch (mark.type) {
              case 'strong':
                text = `<strong>${text}</strong>`;
                break;
              case 'em':
                text = `<em>${text}</em>`;
                break;
              case 'code':
                text = `<code>${text}</code>`;
                break;
              case 'underline':
                text = `<u>${text}</u>`;
                break;
              case 'strike':
                text = `<s>${text}</s>`;
                break;
              case 'link': {
                // safeUrl drops javascript:/data:/vbscript: schemes.
                const href = safeUrl(mark.attrs?.href);
                text = `<a href="${esc(href)}" target="_blank" rel="noopener noreferrer">${text}</a>`;
                break;
              }
              case 'textColor': {
                // Constrain to a hex/rgb color so an attacker can't break
                // out of the style attribute.
                const color = safeColor(mark.attrs?.color);
                text = color ? `<span style="color: ${color}">${text}</span>` : text;
                break;
              }
            }
          });
        }

        return text;
      }
      
      // Handle content recursively
      const renderContent = () => {
        if (node.content && Array.isArray(node.content)) {
          return node.content.map(child => renderNode(child)).join('');
        }
        return '';
      };
      
      // Handle different node types
      switch (node.type) {
        case 'paragraph':
          html = `<p>${renderContent()}</p>`;
          break;
          
        case 'heading':
          const level = node.attrs?.level || 1;
          html = `<h${level}>${renderContent()}</h${level}>`;
          break;
          
        case 'bulletList':
          html = `<ul>${renderContent()}</ul>`;
          break;
          
        case 'orderedList':
          html = `<ol>${renderContent()}</ol>`;
          break;
          
        case 'listItem':
          html = `<li>${renderContent()}</li>`;
          break;
          
        case 'codeBlock': {
          // Language is user-controlled but should only ever be a syntax
          // identifier — strip anything that isn't safe for a class attr.
          const language = String(node.attrs?.language || '').replace(/[^a-zA-Z0-9_-]/g, '');
          html = `<pre><code class="language-${language}">${renderContent()}</code></pre>`;
          break;
        }
          
        case 'blockquote':
          html = `<blockquote>${renderContent()}</blockquote>`;
          break;
          
        case 'hardBreak':
          html = '<br>';
          break;
          
        case 'rule':
          html = '<hr>';
          break;
          
        case 'table':
          html = `<table>${renderContent()}</table>`;
          break;
          
        case 'tableRow':
          html = `<tr>${renderContent()}</tr>`;
          break;
          
        case 'tableCell':
        case 'tableHeader': {
          const tag = node.type === 'tableHeader' ? 'th' : 'td';
          // Coerce span attrs to small positive integers — they end up in
          // an attribute and must not carry user-supplied junk.
          const colspan = Math.max(1, Math.min(100, parseInt(node.attrs?.colspan, 10) || 1));
          const rowspan = Math.max(1, Math.min(100, parseInt(node.attrs?.rowspan, 10) || 1));
          html = `<${tag} colspan="${colspan}" rowspan="${rowspan}">${renderContent()}</${tag}>`;
          break;
        }

        case 'mention': {
          const mentionText = String(node.attrs?.text || node.attrs?.id || '');
          // Only add @ if the text doesn't already start with it; escape it.
          const display = mentionText.startsWith('@') ? mentionText : '@' + mentionText;
          html = `<span class="mention">${esc(display)}</span>`;
          break;
        }

        case 'emoji':
          html = esc(node.attrs?.text || '');
          break;

        case 'inlineCard':
        case 'blockCard': {
          const url = safeUrl(node.attrs?.url);
          html = `<a href="${esc(url)}" target="_blank" rel="noopener noreferrer">${esc(url)}</a>`;
          break;
        }
          
        case 'mediaSingle':
          // MediaSingle wraps a media node
          html = `<div class="media-single">${renderContent()}</div>`;
          break;

        case 'mediaGroup':
          // MediaGroup contains multiple media nodes
          html = `<div class="media-group">${renderContent()}</div>`;
          break;

        case 'media':
          // Handle actual media content (images, files)
          const mediaAttrs = node.attrs || {};
          const mediaId = mediaAttrs.id;
          const mediaType = mediaAttrs.type || 'file';
          const collection = mediaAttrs.collection || '';
          const mediaAlt = mediaAttrs.alt || 'Media';
          const mediaUrl = mediaAttrs.url;

          // Debug: log media node to understand structure
          console.log('[ADF Media Node]', { mediaId, mediaType, collection, mediaAlt, mediaUrl, allAttrs: mediaAttrs });

          // mediaId here is an HMAC-signed token from app-api (see
          // mediaTokenService.js); user-bound and 1h-expiring. No ?token=
          // query param — auth flows through the session cookie.
          {
            const fileName = mediaAttrs.fileName || mediaAttrs.alt || 'Attachment';
            const mime = String(mediaAttrs.mimeType || '');
            const imageExtRe = /\.(jpg|jpeg|png|gif|webp|svg|bmp|heic|heif|avif)(\?|$)/i;
            const isImage = mime.startsWith('image/')
              || imageExtRe.test(fileName)
              || imageExtRe.test(mediaAlt)
              || (mediaUrl && imageExtRe.test(mediaUrl));
            const rawUrl = mediaUrl || (mediaId ? `/api/jira/media/${mediaId}` : null);
            const url = rawUrl ? safeUrl(rawUrl) : null;

            if (!url) {
              html = `<div class="media-placeholder" style="padding: 8px; background: var(--bg-secondary, #f5f5f5); border-radius: 4px; color: var(--text-secondary, #666); font-style: italic;">[Media content - unable to load]</div>`;
            } else {
              // Pick an emoji for the file icon based on MIME
              let icon = '📎';
              if (isImage) icon = '🖼️';
              else if (mime === 'application/pdf') icon = '📄';
              else if (mime.startsWith('video/')) icon = '🎬';
              else if (mime.startsWith('audio/')) icon = '🎵';
              else if (mime.startsWith('text/')) icon = '📝';
              else if (/zip|tar|gzip|rar|7z/.test(mime)) icon = '📦';

              const safeUrlAttr = esc(url);
              const safeFileName = esc(fileName);
              const thumb = isImage
                ? `<img src="${safeUrlAttr}" alt="" loading="lazy" style="width: 40px; height: 40px; object-fit: cover; border-radius: 3px; flex-shrink: 0; background: var(--bg-tertiary, #eee);">`
                : `<span style="width: 40px; height: 40px; display: inline-flex; align-items: center; justify-content: center; font-size: 22px; flex-shrink: 0; background: var(--bg-tertiary, #eee); border-radius: 3px;">${icon}</span>`;

              html = `<a href="${safeUrlAttr}" target="_blank" rel="noopener noreferrer" class="comment-media-card" style="display: inline-flex; align-items: center; gap: 8px; padding: 6px 10px 6px 6px; max-width: 320px; background: var(--bg-secondary, #f5f5f5); border: 1px solid var(--border-color, #e0e0e0); border-radius: 6px; text-decoration: none; color: var(--text-primary, #333); margin: 4px 4px 4px 0; vertical-align: middle;">${thumb}<span style="font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0;">${safeFileName}</span></a>`;
            }
          }
          break;
          
        default:
          // For unknown types, just render content
          html = renderContent();
      }
      
      return html;
    };
    
    return content.content
      .map(node => renderNode(node))
      .join('');
  };

  const fetchComments = async (issueKey, startAt = 0) => {
    setCommentsLoading(true);
    try {
      const response = await fetch(`/api/jira/comments/${issueKey}?startAt=${startAt}&maxResults=20`, {
        headers: {
          Authorization: getAuthHeader()
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }
      
      const data = await response.json();
      if (startAt === 0) {
        setIssueComments(data.comments || []);
      } else {
        setIssueComments(prev => [...prev, ...(data.comments || [])]);
      }
      setCommentsTotal(data.total || 0);
      setCommentsStartAt((data.startAt || 0) + (data.comments?.length || 0));
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setCommentsLoading(false);
    }
  };
  
  // Pin/unpin issue
  const togglePin = async (issueKey) => {
    const isPinned = pinnedIssues.includes(issueKey);
    const newPinned = isPinned
      ? pinnedIssues.filter(k => k !== issueKey)
      : [issueKey, ...pinnedIssues];

    setPinnedIssues(newPinned);

    // Mark as recently accessed
    markIssueAsRecent(issueKey);

    // Reset rank for this item - the hook will handle re-ranking

    // Close swipe
    setSwipeStates(prev => ({
      ...prev,
      [issueKey]: { x: 0, settled: true }
    }));
    
    try {
      await fetch('/api/jira/pin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({ 
          issueKey, 
          pinned: !isPinned 
        })
      });
      
      // Reset the rank for this issue
      await fetch('/api/jira/sort', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({
          view: viewMode.toLowerCase(),
          updates: { [issueKey]: null } // Remove rank to reset position
        })
      });
    } catch (err) {
      console.error('Failed to update pin status:', err);
      // Revert on error
      setPinnedIssues(pinnedIssues);
    }
  };
  
  // Set issue color
  const setIssueColor = async (issueKey, color) => {
    // console.log('setIssueColor called:', { issueKey, color, currentColors: issueColors });
    
    let newColors;
    if (color === null || color === undefined) {
      // Remove color
      newColors = { ...issueColors };
      delete newColors[issueKey];
    } else {
      // Set color
      newColors = { ...issueColors, [issueKey]: color };
    }
    
    // console.log('New colors state:', newColors);

    // Update state immediately for instant visual feedback
    setIssueColors(newColors);

    // Mark as recently accessed
    markIssueAsRecent(issueKey);

    // DON'T close the swipe menu - let user continue cycling
    // Only close on pin/unpin or other actions
    
    try {
      const response = await fetch('/api/jira/color', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({ issueKey, color })
      });
      // console.log('Color API response:', response.status);
    } catch (err) {
      console.error('Failed to update color:', err);
      // Revert on error
      setIssueColors(issueColors);
    }
  };
  
  // Split ordered issues into Active and Done sections - memoized to prevent re-renders
  const sortedIssues = useMemo(() => {
    const sortStart = performance.now();

    if (!finalOrderedIssues || finalOrderedIssues.length === 0) return { active: [], done: [] };

    // For RECENT mode, don't split into active/done - show all issues in order
    if (viewMode === VIEW_MODES.RECENT) {
      return {
        active: finalOrderedIssues,
        done: []
      };
    }

    const active = [];
    const done = [];

    // Split ordered issues into Active and Done, maintaining order from hook
    finalOrderedIssues.forEach(issue => {
      const status = issue.fields?.status?.name?.toLowerCase() || '';
      // Check for done-like statuses (case-insensitive)
      const isDone = status.includes('done') ||
                     status.includes('on hold') ||
                     status.includes('onhold') ||
                     status.includes('closed') ||
                     status.includes('resolved') ||
                     status.includes('complete');

      if (isDone) {
        done.push(issue);
      } else {
        active.push(issue);
      }
    });

    // Within each section, render pinned items first (in pinnedIssues order), then unpinned
    // This keeps ranks stable - we just change display order
    const sortByPinned = (issues) => {
      const pinned = issues.filter(issue => pinnedIssues.includes(issue.key));
      const unpinned = issues.filter(issue => !pinnedIssues.includes(issue.key));

      // Sort pinned items by their position in the pinnedIssues array
      pinned.sort((a, b) => {
        const aIndex = pinnedIssues.indexOf(a.key);
        const bIndex = pinnedIssues.indexOf(b.key);
        return aIndex - bIndex;
      });

      return [...pinned, ...unpinned];
    };

    const result = {
      active: sortByPinned(active),
      done: sortByPinned(done)
    };

    const sortDuration = performance.now() - sortStart;
    if (sortDuration > 10) {
      console.log('[JiraDashboard] sortedIssues memo:', { duration: `${sortDuration.toFixed(0)}ms`, activeCount: result.active.length, doneCount: result.done.length });
    }

    return result;
  }, [finalOrderedIssues, pinnedIssues, viewMode]);

  
  // Handle transcript received from RecordingModal
  const handleTranscriptReceived = (result) => {
    // Refresh issues if transactions were committed or something was changed
    if (result.transactionsCommitted || 
        result.answer?.includes('updated') ||
        result.answer?.includes('created') ||
        result.answer?.includes('added') ||
        result.answer?.includes('changed')) {
      refresh();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Format date
  const formatDate = (dateStr, isCompletedDate = false) => {
    if (!dateStr) return isCompletedDate ? '' : 'No due date';
    const date = new Date(dateStr);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (isCompletedDate) {
      // For completed dates, show relative time in the past
      if (diffDays === 0) return 'Completed today';
      if (diffDays === -1) return 'Yesterday';
      if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      // For due dates
      if (diffDays === 0) return 'Due Today';
      if (diffDays === 1) return 'Due Tomorrow';
      if (diffDays === -1) return 'Due Yesterday';
      if (diffDays < -1) return `${Math.abs(diffDays)} days overdue`;
      if (diffDays > 1 && diffDays <= 7) return `Due in ${diffDays} days`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };
  
  return (
    <div className="jira-dashboard">
      {/* View mode switcher */}
      <div className="jira-view-switcher">
        <div className="view-switcher-content">
          <div className="view-mode-tabs">
            <button
              className={`view-mode-tab ${viewMode === VIEW_MODES.MY ? 'active' : ''}`}
              onClick={() => setViewMode(VIEW_MODES.MY)}
            >
              My
            </button>
            <button
              className={`view-mode-tab ${viewMode === VIEW_MODES.OTHERS ? 'active' : ''}`}
              onClick={() => setViewMode(VIEW_MODES.OTHERS)}
            >
              Others
            </button>
            <button
              className={`view-mode-tab ${viewMode === VIEW_MODES.RECENT ? 'active' : ''}`}
              onClick={() => setViewMode(VIEW_MODES.RECENT)}
            >
              Recent
            </button>
          </div>
          <button
            className={`jira-refresh-button ${loading ? 'loading' : ''}`}
            onClick={refresh}
            title={loading ? "Refreshing..." : "Refresh issues"}
            disabled={loading || preferencesLoading}
          >
            {loading ? (
              <img
                src="/clock.gif"
                alt="Loading"
                width="18"
                height="18"
                className="refresh-loading-icon"
              />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            )}
          </button>
        </div>
        <div className="view-mode-underline"></div>
      </div>

      {/* Search panel */}
      <div className="search-panel">
        <div className="search-input-wrapper">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search issues…"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              // Clear smart search when typing
              if (smartSearchIds !== null) setSmartSearchIds(null);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleSmartSearch();
              }
            }}
            className="search-input"
          />
          {smartSearchIds !== null ? (
            <button
              onClick={() => setSmartSearchIds(null)}
              className="search-button clear-button"
              title="Clear AI search"
            >
              ✕
            </button>
          ) : (
            <button
              onClick={handleSmartSearch}
              disabled={!searchQuery.trim() || smartSearchLoading}
              className="search-button ai-button"
              title="AI-powered smart search (Ctrl+Enter)"
            >
              {smartSearchLoading ? '⏳' : '✨'}
            </button>
          )}
        </div>

        {/* Filter selectors */}
        <div className="filter-selectors">
          {/* Status filter */}
          <div className="custom-dropdown">
            <button
              className={`dropdown-trigger ${filters.status.length > 0 ? 'active' : ''}`}
              onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
            >
              <span className="dropdown-label">
                {filters.status.length > 0 ? `Status (${filters.status.length})` : 'Status'}
              </span>
              <svg className="dropdown-arrow" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <path d="M5 6L2.5 3.5h5L5 6z"/>
              </svg>
            </button>
            {openDropdown === 'status' && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => {
                  setFilters(prev => ({ ...prev, status: [] }));
                }}>
                  <span>All</span>
                  <span className="checkbox">{filters.status.length === 0 ? '●' : '○'}</span>
                </div>
                {filterOptions.statuses.map(status => (
                  <div
                    key={status}
                    className="dropdown-item"
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        status: prev.status.includes(status)
                          ? prev.status.filter(s => s !== status)
                          : [...prev.status, status]
                      }));
                    }}
                  >
                    <span>{status}</span>
                    <span className="checkbox">{filters.status.includes(status) ? '●' : '○'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assignee filter */}
          <div className="custom-dropdown">
            <button
              className={`dropdown-trigger ${filters.assignee.length > 0 ? 'active' : ''}`}
              onClick={() => setOpenDropdown(openDropdown === 'assignee' ? null : 'assignee')}
            >
              <span className="dropdown-label">
                {filters.assignee.length > 0 ? `Assignee (${filters.assignee.length})` : 'Assignee'}
              </span>
              <svg className="dropdown-arrow" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <path d="M5 6L2.5 3.5h5L5 6z"/>
              </svg>
            </button>
            {openDropdown === 'assignee' && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => {
                  setFilters(prev => ({ ...prev, assignee: [] }));
                }}>
                  <span>All</span>
                  <span className="checkbox">{filters.assignee.length === 0 ? '●' : '○'}</span>
                </div>
                {filterOptions.assignees.map(assignee => (
                  <div
                    key={assignee}
                    className="dropdown-item"
                    onClick={() => {
                      const isMe = assignee === user.name;
                      const wasSelected = filters.assignee.includes(assignee);

                      if (isMe) {
                        // Selecting myself - clear all others and switch to My view
                        if (wasSelected) {
                          // Deselecting myself
                          setFilters(prev => ({
                            ...prev,
                            assignee: prev.assignee.filter(a => a !== assignee)
                          }));
                        } else {
                          // Selecting myself - clear all others
                          setFilters(prev => ({ ...prev, assignee: [assignee] }));
                          setViewMode(VIEW_MODES.MY);
                        }
                      } else {
                        // Selecting someone else - deselect me and switch to Others view
                        if (wasSelected) {
                          // Deselecting this person
                          setFilters(prev => ({
                            ...prev,
                            assignee: prev.assignee.filter(a => a !== assignee)
                          }));
                        } else {
                          // Selecting this person - remove me and add them
                          setFilters(prev => ({
                            ...prev,
                            assignee: [...prev.assignee.filter(a => a !== user.name), assignee]
                          }));
                          setViewMode(VIEW_MODES.OTHERS);
                        }
                      }
                    }}
                  >
                    <span>{assignee}</span>
                    <span className="checkbox">{filters.assignee.includes(assignee) ? '●' : '○'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Priority filter */}
          <div className="custom-dropdown">
            <button
              className={`dropdown-trigger ${filters.priority.length > 0 ? 'active' : ''}`}
              onClick={() => setOpenDropdown(openDropdown === 'priority' ? null : 'priority')}
            >
              <span className="dropdown-label">
                {filters.priority.length > 0 ? `Priority (${filters.priority.length})` : 'Priority'}
              </span>
              <svg className="dropdown-arrow" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <path d="M5 6L2.5 3.5h5L5 6z"/>
              </svg>
            </button>
            {openDropdown === 'priority' && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => {
                  setFilters(prev => ({ ...prev, priority: [] }));
                }}>
                  <span>All</span>
                  <span className="checkbox">{filters.priority.length === 0 ? '●' : '○'}</span>
                </div>
                {filterOptions.priorities.map(priority => (
                  <div
                    key={priority}
                    className="dropdown-item"
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        priority: prev.priority.includes(priority)
                          ? prev.priority.filter(p => p !== priority)
                          : [...prev.priority, priority]
                      }));
                    }}
                  >
                    <span>{priority}</span>
                    <span className="checkbox">{filters.priority.includes(priority) ? '●' : '○'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Type filter */}
          <div className="custom-dropdown">
            <button
              className={`dropdown-trigger ${filters.type.length > 0 ? 'active' : ''}`}
              onClick={() => setOpenDropdown(openDropdown === 'type' ? null : 'type')}
            >
              <span className="dropdown-label">
                {filters.type.length > 0 ? `Type (${filters.type.length})` : 'Type'}
              </span>
              <svg className="dropdown-arrow" width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <path d="M5 6L2.5 3.5h5L5 6z"/>
              </svg>
            </button>
            {openDropdown === 'type' && (
              <div className="dropdown-menu">
                <div className="dropdown-item" onClick={() => {
                  setFilters(prev => ({ ...prev, type: [] }));
                }}>
                  <span>All</span>
                  <span className="checkbox">{filters.type.length === 0 ? '●' : '○'}</span>
                </div>
                {filterOptions.types.map(type => (
                  <div
                    key={type}
                    className="dropdown-item"
                    onClick={() => {
                      setFilters(prev => ({
                        ...prev,
                        type: prev.type.includes(type)
                          ? prev.type.filter(t => t !== type)
                          : [...prev.type, type]
                      }));
                    }}
                  >
                    <span>{type}</span>
                    <span className="checkbox">{filters.type.includes(type) ? '●' : '○'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Status info */}
      {smartSearchIds !== null && (
        <div style={{ padding: '4px 12px', fontSize: '13px', color: '#666', background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <span style={{ color: '#6366f1', fontWeight: 500 }}>
            ✨ AI filtered: {issues.length} results
          </span>
        </div>
      )}

      {/* Issues list */}
      <div
        className="jira-issues-container"
        ref={containerRef}
        onScroll={handleScroll}
      >
        {(loading || preferencesLoading) && (
          <div className="jira-loading">
            {loading ? 'Loading issues...' : 'Loading preferences...'}
          </div>
        )}
        
        {error && (
          <div className="jira-error">Error: {error}</div>
        )}

        {!loading && !preferencesLoading && !error && sortedIssues.active.length === 0 && sortedIssues.done.length === 0 && (
          <div className="jira-empty">No issues found</div>
        )}

        {/* Active Issues Section */}
        {!preferencesLoading && sortedIssues.active.length > 0 && (
          <div className="active-section">
            {viewMode !== VIEW_MODES.RECENT && (
              <div className="section-header">Active Issues</div>
            )}
            {/* First position drop zone */}
            {isDraggingSort && dragOverIssue === '__first__' && (
              <div className="drop-indicator-first" />
            )}
            {sortedIssues.active.map(issue => {
          const swipeState = swipeStates[issue.key] || { x: 0, settled: true, dragging: false };
          
          return (
          <div
            key={issue.key}
            className={`jira-issue-wrapper ${draggedIssue?.key === issue.key ? 'dragging' : ''} ${dragOverIssue === issue.key ? (dropPosition === 'before' ? 'drag-over-before' : 'drag-over-after') : ''}`}
            data-issue-key={issue.key}
          >
            {/* Left menu - behind the row, only visible when swiped */}
            <div className="swipe-menu swipe-menu-left" style={{
              opacity: swipeState.x > 0 ? Math.min(swipeState.x / 160, 1) : 0,
              visibility: swipeState.x > 0 ? 'visible' : 'hidden'
            }}>
              <div
                className="swipe-action pin-action"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePin(issue.key);
                }}
              >
                <span className="action-icon">📌</span>
                <span className="action-text">{pinnedIssues.includes(issue.key) ? 'Unpin' : 'Pin'}</span>
              </div>
              <div
                className={`swipe-action color-action ${issueColors[issue.key] ? `current-${issueColors[issue.key].toLowerCase()}` : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Cycle through colors: none -> red -> yellow -> green -> blue -> none
                  const colors = [null, 'RED', 'YELLOW', 'GREEN', 'LIGHTBLUE'];
                  const currentColor = issueColors[issue.key] || null;
                  const currentIndex = colors.indexOf(currentColor);
                  const nextColor = colors[(currentIndex + 1) % colors.length];
                  // console.log('Color cycling:', { issueKey: issue.key, currentColor, nextColor, currentIndex });
                  setIssueColor(issue.key, nextColor);
                }}
              >
                <span className="action-icon" style={{ marginBottom: 0 }}>
                  {issueColors[issue.key] ? '●' : '○'}
                </span>
              </div>
            </div>
            
            {/* Right menu - behind the row, only visible when swiped */}
            <div className="swipe-menu swipe-menu-right" style={{
              opacity: swipeState.x < 0 ? Math.min(Math.abs(swipeState.x) / 160, 1) : 0,
              visibility: swipeState.x < 0 ? 'visible' : 'hidden'
            }}>
              <div
                className="swipe-action transition-action"
                onClick={async (e) => {
                  e.stopPropagation();
                  // Close swipe menu
                  setSwipeStates(prev => ({
                    ...prev,
                    [issue.key]: { x: 0, settled: true, dragging: false }
                  }));
                  // Open transitions menu
                  setTransitionIssue(issue);
                  setLoadingTransitions(true);
                  
                  // Fetch available transitions
                  try {
                    // console.log(`Fetching transitions for ${issue.key}`);
                    const response = await fetch(`/api/jira/transitions/${issue.key}`, {
                      headers: { Authorization: getAuthHeader() }
                    });
                    // console.log('Transitions response status:', response.status);
                    if (response.ok) {
                      const data = await response.json();
                      // console.log('Transitions data:', data);
                      // Sort transitions: Todo, In Progress, Scoped, Done, On Hold
                      const sortOrder = ['todo', 'in progress', 'scoped', 'done', 'on hold'];
                      const sorted = (data.transitions || []).sort((a, b) => {
                        const aName = (a.to?.name || a.name || '').toLowerCase();
                        const bName = (b.to?.name || b.name || '').toLowerCase();
                        const aIndex = sortOrder.findIndex(s => aName.includes(s));
                        const bIndex = sortOrder.findIndex(s => bName.includes(s));
                        if (aIndex === -1 && bIndex === -1) return 0;
                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;
                        return aIndex - bIndex;
                      });
                      setAvailableTransitions(sorted);
                    } else {
                      const errorText = await response.text();
                      console.error('Failed to fetch transitions:', response.status, errorText);
                      setAvailableTransitions([]);
                    }
                  } catch (error) {
                    console.error('Error fetching transitions:', error);
                    setAvailableTransitions([]);
                  } finally {
                    setLoadingTransitions(false);
                  }
                }}
              >
                <span className="action-icon">→</span>
                <span className="action-text">Move</span>
              </div>
              <div
                className="swipe-action ai-action"
                onClick={(e) => {
                  e.stopPropagation();
                  // Close swipe menu
                  setSwipeStates(prev => ({
                    ...prev,
                    [issue.key]: { x: 0, settled: true, dragging: false }
                  }));
                  // Open AI recording modal
                  setRecordingContext({
                    issueKey: issue.key,
                    summary: issue.fields?.summary
                  });
                  // Mark as recently accessed
                  markIssueAsRecent(issue.key);
                  setRecordingPopupOpen(true);
                }}
              >
                <span className="action-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C10.895 2 10 2.895 10 4V12C10 13.105 10.895 14 12 14C13.105 14 14 13.105 14 12V4C14 2.895 13.105 2 12 2ZM7 12H5C5 15.53 7.611 18.4 11 18.92V22H13V18.92C16.389 18.4 19 15.53 19 12H17C17 14.761 14.761 17 12 17C9.239 17 7 14.761 7 12Z"/>
                  </svg>
                </span>
                <span className="action-text">AI</span>
              </div>
            </div>
            
            {/* The actual row that swipes */}
            <div
              className={`jira-issue-row 
                ${pinnedIssues.includes(issue.key) ? 'pinned' : ''} 
                ${issueColors[issue.key] ? `color-${issueColors[issue.key].toLowerCase()}` : ''}
                ${droppedIssueKey === issue.key ? 'just-dropped' : ''}`}
              style={{
                transform: `translateX(${swipeState.x || 0}px)`,
                transition: (swipeState.dragging || isDragging.current) ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 2,
                display: isDraggingSort && draggedIssue?.key === issue.key ? 'none' : 'block'
              }}
              onTouchStart={(e) => {
                handleSwipeStart(e, issue.key);
                handleLongPressStart(e, issue);
              }}
              onTouchMove={(e) => {
                handleSwipeMove(e, issue.key);
                handleLongPressMove(e);
              }}
              onTouchEnd={(e) => {
                handleSwipeEnd(e, issue.key);
                handleLongPressEnd(e);
              }}
              onMouseDown={(e) => {
                // Ignore non-primary buttons. A right-click on the row was
                // starting the swipe/long-press flow and leaving its state
                // half-applied once the browser context menu stole focus,
                // which made the issue appear to disappear.
                if (e.button !== 0) return;
                handleSwipeStart(e, issue.key);
                handleLongPressStart(e, issue);
              }}
              onMouseMove={(e) => handleLongPressMove(e)}
              onMouseUp={(e) => handleLongPressEnd(e)}
              onClick={(e) => handleRowClick(e, issue)}
            >
              {/* Issue type icon */}
              <img
                src={`/assets/issue-types/${
                  issue.fields?.issuetype?.name === 'Sub-task' ? 'subtask' :
                  issue.fields?.issuetype?.name === 'Goal' ? 'goal' :
                  'task'
                }.svg`}
                alt={issue.fields?.issuetype?.name || 'Task'}
                className="issue-type-icon"
              />

              {/* Pin icon */}
              {pinnedIssues.includes(issue.key) && <span className="pin-icon">📌</span>}

              {/* Issue content */}
            <div className="issue-content" draggable="false" onDragStart={(e) => e.preventDefault()}>
              <div className="issue-title">
                {obfuscate(issue.fields?.summary) || 'Untitled'}
              </div>
              <div className="issue-meta">
                <span className={`issue-key ${
                  (() => {
                    const statusName = issue.fields?.status?.name?.toLowerCase() || '';
                    return (
                      statusName.includes('done') ||
                      statusName.includes('hold') ||
                      statusName.includes('closed') ||
                      statusName.includes('resolved') ||
                      statusName.includes('complete') ||
                      statusName.includes('finished') ||
                      statusName === 'on hold'
                    ) ? 'strikethrough' : '';
                  })()
                }`}>{issue.key}</span>
                <span className="issue-assignee">
                  {issue.assignee?.avatarUrl && (
                    <img
                      src={issue.assignee.avatarUrl}
                      alt={issue.assignee.display}
                      className="assignee-avatar"
                    />
                  )}
                  {issue.assignee?.display || issue.fields?.assignee?.displayName || 'Unassigned'}
                </span>
                <span className="issue-status" data-status={issue.fields?.status?.name || 'Unknown'}>{issue.fields?.status?.name || 'Unknown'}</span>
              </div>
              {(() => {
                const status = issue.fields?.status?.name?.toLowerCase() || '';
                const isDone = status.includes('done') ||
                              status.includes('on hold') ||
                              status.includes('onhold') ||
                              status.includes('closed') ||
                              status.includes('resolved') ||
                              status.includes('complete');

                if (isDone && (issue.fields?.resolutiondate || issue.fields?.statuscategorychangedate || issue.fields?.updated)) {
                  return (
                    <div className="issue-completed-line">
                      {formatDate(issue.fields?.resolutiondate || issue.fields?.statuscategorychangedate || issue.fields?.updated, true)}
                    </div>
                  );
                } else if (issue.fields?.duedate) {
                  return (
                    <div className={`issue-due-line ${new Date(issue.fields.duedate) < new Date() ? 'overdue' : ''}`}>
                      {formatDate(issue.fields?.duedate)}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            
            </div>
          </div>
          );
        })}
          </div>
        )}

        {/* Done Issues Section */}
        {!preferencesLoading && sortedIssues.done.length > 0 && (
          <div className="done-section">
            <div className="section-header">Done</div>
            {sortedIssues.done.map(issue => {
          const swipeState = swipeStates[issue.key] || { x: 0, settled: true, dragging: false };
          
          return (
          <div
            key={issue.key}
            className={`jira-issue-wrapper ${draggedIssue?.key === issue.key ? 'dragging' : ''} ${dragOverIssue === issue.key ? (dropPosition === 'before' ? 'drag-over-before' : 'drag-over-after') : ''}`}
            data-issue-key={issue.key}
          >
            {/* Left menu - behind the row, only visible when swiped */}
            <div className="swipe-menu swipe-menu-left" style={{
              opacity: swipeState.x > 0 ? Math.min(swipeState.x / 160, 1) : 0,
              visibility: swipeState.x > 0 ? 'visible' : 'hidden'
            }}>
              <div
                className="swipe-action pin-action"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePin(issue.key);
                }}
              >
                <span className="action-icon">📌</span>
                <span className="action-text">{pinnedIssues.includes(issue.key) ? 'Unpin' : 'Pin'}</span>
              </div>
              <div
                className={`swipe-action color-action ${issueColors[issue.key] ? `current-${issueColors[issue.key].toLowerCase()}` : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Cycle through colors: none -> red -> yellow -> green -> blue -> none
                  const colors = [null, 'RED', 'YELLOW', 'GREEN', 'LIGHTBLUE'];
                  const currentColor = issueColors[issue.key] || null;
                  const currentIndex = colors.indexOf(currentColor);
                  const nextColor = colors[(currentIndex + 1) % colors.length];
                  // console.log('Color cycling:', { issueKey: issue.key, currentColor, nextColor, currentIndex });
                  setIssueColor(issue.key, nextColor);
                }}
              >
                <span className="action-icon" style={{ marginBottom: 0 }}>
                  {issueColors[issue.key] ? '●' : '○'}
                </span>
              </div>
            </div>
            
            {/* Right menu - behind the row, only visible when swiped */}
            <div className="swipe-menu swipe-menu-right" style={{
              opacity: swipeState.x < 0 ? Math.min(Math.abs(swipeState.x) / 160, 1) : 0,
              visibility: swipeState.x < 0 ? 'visible' : 'hidden'
            }}>
              <div
                className="swipe-action transition-action"
                onClick={async (e) => {
                  e.stopPropagation();
                  // Close swipe menu
                  setSwipeStates(prev => ({
                    ...prev,
                    [issue.key]: { x: 0, settled: true, dragging: false }
                  }));
                  // Open transitions menu
                  setTransitionIssue(issue);
                  setLoadingTransitions(true);
                  
                  // Fetch available transitions
                  try {
                    // console.log(`Fetching transitions for ${issue.key}`);
                    const response = await fetch(`/api/jira/transitions/${issue.key}`, {
                      headers: { Authorization: getAuthHeader() }
                    });
                    // console.log('Transitions response status:', response.status);
                    if (response.ok) {
                      const data = await response.json();
                      // console.log('Transitions data:', data);
                      // Sort transitions: Todo, In Progress, Scoped, Done, On Hold
                      const sortOrder = ['todo', 'in progress', 'scoped', 'done', 'on hold'];
                      const sorted = (data.transitions || []).sort((a, b) => {
                        const aName = (a.to?.name || a.name || '').toLowerCase();
                        const bName = (b.to?.name || b.name || '').toLowerCase();
                        const aIndex = sortOrder.findIndex(s => aName.includes(s));
                        const bIndex = sortOrder.findIndex(s => bName.includes(s));
                        if (aIndex === -1 && bIndex === -1) return 0;
                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;
                        return aIndex - bIndex;
                      });
                      setAvailableTransitions(sorted);
                    } else {
                      const errorText = await response.text();
                      console.error('Failed to fetch transitions:', response.status, errorText);
                      setAvailableTransitions([]);
                    }
                  } catch (error) {
                    console.error('Error fetching transitions:', error);
                    setAvailableTransitions([]);
                  } finally {
                    setLoadingTransitions(false);
                  }
                }}
              >
                <span className="action-icon">→</span>
                <span className="action-text">Move</span>
              </div>
              <div
                className="swipe-action ai-action"
                onClick={(e) => {
                  e.stopPropagation();
                  // Close swipe menu
                  setSwipeStates(prev => ({
                    ...prev,
                    [issue.key]: { x: 0, settled: true, dragging: false }
                  }));
                  // Open AI recording modal
                  setRecordingContext({
                    issueKey: issue.key,
                    summary: issue.fields?.summary
                  });
                  // Mark as recently accessed
                  markIssueAsRecent(issue.key);
                  setRecordingPopupOpen(true);
                }}
              >
                <span className="action-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C10.895 2 10 2.895 10 4V12C10 13.105 10.895 14 12 14C13.105 14 14 13.105 14 12V4C14 2.895 13.105 2 12 2ZM7 12H5C5 15.53 7.611 18.4 11 18.92V22H13V18.92C16.389 18.4 19 15.53 19 12H17C17 14.761 14.761 17 12 17C9.239 17 7 14.761 7 12Z"/>
                  </svg>
                </span>
                <span className="action-text">AI</span>
              </div>
            </div>
            
            {/* The actual row that swipes */}
            <div
              className={`jira-issue-row 
                ${pinnedIssues.includes(issue.key) ? 'pinned' : ''} 
                ${issueColors[issue.key] ? `color-${issueColors[issue.key].toLowerCase()}` : ''}
                ${droppedIssueKey === issue.key ? 'just-dropped' : ''}`}
              style={{
                transform: `translateX(${swipeState.x || 0}px)`,
                transition: (swipeState.dragging || isDragging.current) ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 2,
                display: isDraggingSort && draggedIssue?.key === issue.key ? 'none' : 'block'
              }}
              onTouchStart={(e) => {
                handleSwipeStart(e, issue.key);
                handleLongPressStart(e, issue);
              }}
              onTouchMove={(e) => {
                handleSwipeMove(e, issue.key);
                handleLongPressMove(e);
              }}
              onTouchEnd={(e) => {
                handleSwipeEnd(e, issue.key);
                handleLongPressEnd(e);
              }}
              onMouseDown={(e) => {
                // Ignore non-primary buttons. A right-click on the row was
                // starting the swipe/long-press flow and leaving its state
                // half-applied once the browser context menu stole focus,
                // which made the issue appear to disappear.
                if (e.button !== 0) return;
                handleSwipeStart(e, issue.key);
                handleLongPressStart(e, issue);
              }}
              onMouseMove={(e) => handleLongPressMove(e)}
              onMouseUp={(e) => handleLongPressEnd(e)}
              onClick={(e) => handleRowClick(e, issue)}
            >
              {/* Issue type icon */}
              <img
                src={`/assets/issue-types/${
                  issue.fields?.issuetype?.name === 'Sub-task' ? 'subtask' :
                  issue.fields?.issuetype?.name === 'Goal' ? 'goal' :
                  'task'
                }.svg`}
                alt={issue.fields?.issuetype?.name || 'Task'}
                className="issue-type-icon"
              />

              {/* Pin icon */}
              {pinnedIssues.includes(issue.key) && <span className="pin-icon">📌</span>}

              {/* Issue content */}
            <div className="issue-content" draggable="false" onDragStart={(e) => e.preventDefault()}>
              <div className="issue-title">
                {obfuscate(issue.fields?.summary) || 'Untitled'}
              </div>
              <div className="issue-meta">
                <span className={`issue-key ${
                  (() => {
                    const statusName = issue.fields?.status?.name?.toLowerCase() || '';
                    return (
                      statusName.includes('done') ||
                      statusName.includes('hold') ||
                      statusName.includes('closed') ||
                      statusName.includes('resolved') ||
                      statusName.includes('complete') ||
                      statusName.includes('finished') ||
                      statusName === 'on hold'
                    ) ? 'strikethrough' : '';
                  })()
                }`}>{issue.key}</span>
                <span className="issue-assignee">
                  {issue.assignee?.avatarUrl && (
                    <img
                      src={issue.assignee.avatarUrl}
                      alt={issue.assignee.display}
                      className="assignee-avatar"
                    />
                  )}
                  {issue.assignee?.display || issue.fields?.assignee?.displayName || 'Unassigned'}
                </span>
                <span className="issue-status" data-status={issue.fields?.status?.name || 'Unknown'}>{issue.fields?.status?.name || 'Unknown'}</span>
              </div>
              {(issue.fields?.resolutiondate || issue.fields?.statuscategorychangedate || issue.fields?.updated) && (
                <div className="issue-completed-line">
                  {formatDate(issue.fields?.resolutiondate || issue.fields?.statuscategorychangedate || issue.fields?.updated, true)}
                </div>
              )}
            </div>
            
            </div>
          </div>
          );
        })}
          </div>
        )}
      </div>
      
      {/* Full issue view modal */}
      {selectedIssue && selectedIssue.key && (
        <div className="jira-full-view">
          <div className="full-view-container">
            {/* Redesigned Header */}
            <div className="full-view-header">
              {/* Top row: Back button, spacer, Jira/AI buttons */}
              <div className="preview-header-row">
                <button
                  className="close-btn"
                  onClick={() => {
                    setSelectedIssue(null);
                    setIssueError(null);
                    setIssueLoading(false);
                    // If we came from another view, return to it
                    if (returnToView && onReturnToView) {
                      onReturnToView(returnToView);
                    }
                  }}
                  aria-label="Back"
                >
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                  </svg>
                </button>
                <div className="spacer"></div>
                <button
                  className="view-in-jira-button"
                  onClick={() => {
                    const jiraBaseUrl = process.env.REACT_APP_JIRA_BASE_URL || '';
                    window.open(`${jiraBaseUrl}/${selectedIssue.key}`, '_blank');
                  }}
                  title="View in Jira"
                >
                  <svg height="20" viewBox="0 0 24 24">
                    <path fill="var(--tile-color,#1868db)" d="M0 6a6 6 0 0 1 6-6h12a6 6 0 0 1 6 6v12a6 6 0 0 1-6 6H6a6 6 0 0 1-6-6z"></path>
                    <path fill="var(--icon-color, white)" d="M9.051 15.434H7.734c-1.988 0-3.413-1.218-3.413-3h7.085c.367 0 .605.26.605.63v7.13c-1.772 0-2.96-1.435-2.96-3.434zm3.5-3.543h-1.318c-1.987 0-3.413-1.196-3.413-2.978h7.085c.367 0 .627.239.627.608v7.13c-1.772 0-2.981-1.435-2.981-3.434zm3.52-3.522h-1.317c-1.987 0-3.413-1.217-3.413-3h7.085c.367 0 .605.262.605.61v7.129c-1.771 0-2.96-1.435-2.96-3.434z"></path>
                  </svg>
                </button>
                <button
                  className="issue-ai-button"
                  onClick={() => {
                    setRecordingContext({
                      issueKey: selectedIssue.key,
                      summary: selectedIssue.fields?.summary
                    });
                    markIssueAsRecent(selectedIssue.key);
                    setRecordingPopupOpen(true);
                  }}
                  title="AI Command"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z"></path>
                    <path d="M2 17L12 22L22 17"></path>
                    <path d="M2 12L12 17L22 12"></path>
                  </svg>
                </button>
              </div>

              {/* Issue content area with avatar */}
              <div className="preview-content-area">
                {/* Avatar positioned in top right - clickable to change assignee (only when loaded) */}
                {selectedIssue?.assignee?.avatarUrl || selectedIssue?.fields?.assignee?.avatarUrls?.['48x48'] ? (
                  <img
                    src={selectedIssue?.assignee?.avatarUrl || selectedIssue?.fields?.assignee?.avatarUrls?.['48x48']}
                    alt={selectedIssue?.assignee?.display || selectedIssue?.fields?.assignee?.displayName}
                    className="assignee-avatar"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedIssue?.fields) setAssigneePopupOpen(true);
                    }}
                    style={{cursor: selectedIssue?.fields ? 'pointer' : 'default'}}
                  />
                ) : (
                  <div
                    className="assignee-avatar"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedIssue?.fields) setAssigneePopupOpen(true);
                    }}
                    style={{cursor: selectedIssue?.fields ? 'pointer' : 'default', opacity: selectedIssue?.fields ? 1 : 0.5}}
                  >
                    {selectedIssue?.assignee?.display?.[0]?.toUpperCase() ||
                     selectedIssue?.fields?.assignee?.displayName?.[0]?.toUpperCase() ||
                     (selectedIssue?._loading ? '...' : 'U')}
                  </div>
                )}

                {/* Issue key with icon */}
                <div className="preview-issue-key">
                  <img
                    src={`/assets/issue-types/${
                      selectedIssue.fields?.issuetype?.name === 'Sub-task' ? 'subtask' :
                      selectedIssue.fields?.issuetype?.name === 'Goal' ? 'goal' :
                      'task'
                    }.svg`}
                    alt={selectedIssue.fields?.issuetype?.name || 'Task'}
                  />
                  {selectedIssue.key}
                </div>

                {/* Issue title */}
                <h1
                  className="preview-issue-title"
                  style={{cursor: selectedIssue?.fields ? 'pointer' : 'default'}}
                  onClick={() => {
                    if (!selectedIssue?.fields) return;
                    setEditedTitle(selectedIssue?.fields?.summary || '');
                    setIssueTitlePopupOpen(true);
                  }}
                >
                  {obfuscate(selectedIssue?.fields?.summary) || (selectedIssue?._loading ? 'Loading...' : 'Untitled')}
                </h1>
              </div>

              {/* Status button - only interactive when loaded */}
              <div className="issue-status-bar">
                <button
                  className="issue-status"
                  data-status={selectedIssue?.fields?.status?.name || 'Loading'}
                  disabled={!selectedIssue?.fields}
                  style={{ opacity: selectedIssue?.fields ? 1 : 0.6, cursor: selectedIssue?.fields ? 'pointer' : 'default' }}
                  onClick={async () => {
                    if (!selectedIssue?.fields) return;
                    setTransitionIssue(selectedIssue);
                    setLoadingTransitions(true);

                    // Fetch available transitions
                    try {
                      const response = await fetch(`/api/jira/transitions/${selectedIssue.key}`, {
                        headers: { Authorization: getAuthHeader() }
                      });
                      if (response.ok) {
                        const data = await response.json();
                        // Sort transitions: Todo, In Progress, Scoped, Done, On Hold
                        const sortOrder = ['todo', 'in progress', 'scoped', 'done', 'on hold'];
                        const sorted = (data.transitions || []).sort((a, b) => {
                          const aName = (a.to?.name || a.name || '').toLowerCase();
                          const bName = (b.to?.name || b.name || '').toLowerCase();
                          const aIndex = sortOrder.findIndex(s => aName.includes(s));
                          const bIndex = sortOrder.findIndex(s => bName.includes(s));
                          if (aIndex === -1 && bIndex === -1) return 0;
                          if (aIndex === -1) return 1;
                          if (bIndex === -1) return -1;
                          return aIndex - bIndex;
                        });
                        setAvailableTransitions(sorted);
                      }
                    } catch (error) {
                      console.error('Error fetching transitions:', error);
                    } finally {
                      setLoadingTransitions(false);
                    }
                  }}
                >
                  {selectedIssue?.fields?.status?.name || (selectedIssue?._loading ? 'Loading...' : 'Unknown')}
                  {selectedIssue?.fields && <span style={{fontSize: '12px', opacity: 0.85, marginLeft: '6px'}}>▼</span>}
                </button>
              </div>
            </div>

            {/* Error state - issue not found or no access */}
            {issueError && (
              <div className="full-view-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <h2 style={{ margin: '20px 0 8px', fontSize: '18px', color: 'var(--text-color)', fontWeight: 500 }}>
                  Issue Not Available
                </h2>
                <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', maxWidth: '280px' }}>
                  {issueError}
                </p>
                <button
                  onClick={() => {
                    setSelectedIssue(null);
                    setIssueError(null);
                    // If we came from another view, return to it
                    if (returnToView && onReturnToView) {
                      onReturnToView(returnToView);
                    }
                  }}
                  style={{
                    marginTop: '24px',
                    padding: '10px 24px',
                    background: 'var(--primary-color, #0052cc)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Go Back
                </button>
              </div>
            )}

            {/* Loading state - no cached data */}
            {!issueError && selectedIssue?._loading && !selectedIssue?.fields && (
              <div className="full-view-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                <div className="loading-spinner" style={{ width: '40px', height: '40px', border: '3px solid var(--border-color)', borderTopColor: 'var(--primary-color, #0052cc)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <p style={{ marginTop: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  Loading issue...
                </p>
              </div>
            )}

            {/* Normal content - show when we have fields and no error */}
            {!issueError && selectedIssue?.fields && (
            <div className="full-view-content">
              {/* General Card */}
              <section className={`issue-card ${expandedCards['general'] ? 'expanded' : ''}`}>
                <div className="issue-card-header" onClick={() => toggleCard('general')}>
                  <div className="issue-card-header-content">
                    <div className="issue-card-header-title">General</div>
                    <div className="issue-card-header-summary">
                      Priority, Assignee, Due date{selectedIssue?.fields?.description ? ', Description' : ''}
                    </div>
                  </div>
                  <span className="chev">▼</span>
                </div>
                <div className="issue-card-body">
                  {/* Priority */}
                  <div className="card-row" style={{cursor: 'pointer'}} onClick={() => setPriorityPopupOpen(true)}>
                    <div className="row-left">
                      <div className="row-label">Priority</div>
                      <div className="row-value">
                        <div className="priority-indicator">
                          {(() => {
                            const priority = selectedIssue?.fields?.priority?.name || 'Medium';
                            const priorityLower = priority.toLowerCase();

                            if (priorityLower === 'highest') {
                              return (
                                <span className="priority-icon priority-highest">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 11L8 7L12 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M4 7L8 3L12 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </span>
                              );
                            } else if (priorityLower === 'high') {
                              return (
                                <span className="priority-icon priority-high">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 10L8 6L12 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </span>
                              );
                            } else if (priorityLower === 'medium') {
                              return <span className="priority-icon priority-medium">═</span>;
                            } else if (priorityLower === 'low') {
                              return (
                                <span className="priority-icon priority-low">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </span>
                              );
                            } else if (priorityLower === 'lowest') {
                              return (
                                <span className="priority-icon priority-lowest">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M4 5L8 9L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M4 9L8 13L12 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </span>
                              );
                            }
                            return <span className="priority-icon priority-medium">═</span>;
                          })()}
                          <span style={{marginLeft: '8px'}}>{selectedIssue?.fields?.priority?.name || 'Medium'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="row-action">Change</div>
                  </div>

                  {/* Labels */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Labels</div>
                      <div className={`row-value ${!selectedIssue?.fields?.labels?.length ? 'none-value' : ''}`}>
                        {selectedIssue?.fields?.labels?.length > 0
                          ? selectedIssue.fields.labels.join(', ')
                          : 'None'}
                      </div>
                    </div>
                    <div className="row-action">Edit</div>
                  </div>

                  {/* Assignee with avatar */}
                  <div className="card-row" style={{cursor: 'pointer'}} onClick={() => setAssigneePopupOpen(true)}>
                    <div className="row-left">
                      <div className="row-label">Assignee</div>
                      <div className="row-value">
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          {selectedIssue?.fields?.assignee ? (
                            <>
                              {selectedIssue.fields.assignee.avatarUrls?.['24x24'] ? (
                                <img
                                  src={selectedIssue.fields.assignee.avatarUrls['24x24']}
                                  alt={selectedIssue.fields.assignee.displayName}
                                  style={{width: '24px', height: '24px', borderRadius: '50%'}}
                                />
                              ) : (
                                <div style={{
                                  width: '24px', height: '24px', borderRadius: '50%',
                                  background: 'var(--avatar-background, #2b2f38)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '12px', fontWeight: '700', color: '#fff'
                                }}>
                                  {selectedIssue.fields.assignee.displayName[0].toUpperCase()}
                                </div>
                              )}
                              <span>{selectedIssue.fields.assignee.displayName}</span>
                            </>
                          ) : 'Unassigned'}
                        </div>
                      </div>
                    </div>
                    <div className="row-action">Change</div>
                  </div>

                  {/* Due Date */}
                  <div className="card-row" style={{cursor: 'pointer'}} onClick={() => {
                    setEditedDueDate(selectedIssue?.fields?.duedate || '');
                    setDueDatePopupOpen(true);
                  }}>
                    <div className="row-left">
                      <div className="row-label">Due date</div>
                      <div className={`row-value ${!selectedIssue?.fields?.duedate ? 'none-value' : ''}`}>
                        {selectedIssue?.fields?.duedate ? formatDate(selectedIssue.fields.duedate) : 'None'}
                      </div>
                    </div>
                    <div className="row-action">Edit</div>
                  </div>

                  {/* Description */}
                  {selectedIssue?.fields?.description && (
                    <div className="card-row" style={{cursor: 'pointer'}} onClick={() => {
                      // Extract plain text from description for editing
                      const extractText = (content) => {
                        if (typeof content === 'string') return content;
                        if (!content || !content.content) return '';
                        return content.content.map(node => {
                          if (node.type === 'text') return node.text || '';
                          if (node.content) return extractText({content: node.content});
                          return '';
                        }).join('\n');
                      };
                      setEditedDescription(extractText(selectedIssue.fields.description));
                      setDescriptionPopupOpen(true);
                    }}>
                      <div className="row-left">
                        <div className="row-label">Description</div>
                        <div className="row-value" style={{whiteSpace: 'normal', overflow: 'visible'}}>
                          <RichTextHtml
                            className="rich-text"
                            html={convertADFToHTML(selectedIssue.fields.description) || 'No description'}
                          />
                        </div>
                      </div>
                      <div className="row-action">Edit</div>
                    </div>
                  )}

                  {/* Start Date */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Start Date</div>
                      <div className="row-value none-value">None</div>
                    </div>
                    <div className="row-action">Edit</div>
                  </div>

                  {/* Reporter with avatar */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Reporter</div>
                      <div className="row-value">
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          {selectedIssue?.fields?.reporter ? (
                            <>
                              {selectedIssue.fields.reporter.avatarUrls?.['24x24'] ? (
                                <img
                                  src={selectedIssue.fields.reporter.avatarUrls['24x24']}
                                  alt={selectedIssue.fields.reporter.displayName}
                                  style={{width: '24px', height: '24px', borderRadius: '50%'}}
                                />
                              ) : (
                                <div style={{
                                  width: '24px', height: '24px', borderRadius: '50%',
                                  background: 'var(--avatar-background, #2b2f38)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '12px', fontWeight: '700', color: '#fff'
                                }}>
                                  {selectedIssue.fields.reporter.displayName[0].toUpperCase()}
                                </div>
                              )}
                              <span>{selectedIssue.fields.reporter.displayName}</span>
                            </>
                          ) : '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Parent Work Item Card */}
              <section className={`issue-card ${expandedCards['parent'] ? 'expanded' : ''}`}>
                <div className="issue-card-header" onClick={() => {
                  toggleCard('parent');
                  // Debug: log full issue and parent data
                  console.log('Full selectedIssue:', selectedIssue);
                  console.log('selectedIssue.fields:', selectedIssue?.fields);
                  console.log('All field keys:', Object.keys(selectedIssue?.fields || {}));
                  console.log('Parent data:', selectedIssue?.fields?.parent);
                  console.log('Parent check:', !!selectedIssue?.fields?.parent);
                }}>
                  <div className="issue-card-header-content">
                    <div className="issue-card-header-title">Parent work item</div>
                    <div className="issue-card-header-summary">
                      {selectedIssue?.fields?.parent ? selectedIssue.fields.parent.key : 'None'}
                    </div>
                  </div>
                  <span className="chev">▼</span>
                </div>
                <div className="issue-card-body">
                  {selectedIssue?.fields?.parent ? (
                    <div className="parent-item" onClick={() => {
                      // Navigate to the parent issue using navigateToIssue
                      const parentKey = selectedIssue.fields.parent.key;
                      const parentIssue = allIssues.find(i => i.key === parentKey);
                      navigateToIssue(parentKey, parentIssue);
                    }}>
                      <div className="parent-item-main">
                        <img
                          src={`/assets/issue-types/${
                            (selectedIssue.fields.parent.fields?.issuetype?.name || selectedIssue.fields.parent.issuetype?.name) === 'Sub-task' ? 'subtask' :
                            (selectedIssue.fields.parent.fields?.issuetype?.name || selectedIssue.fields.parent.issuetype?.name) === 'Goal' ? 'goal' :
                            'task'
                          }.svg`}
                          alt={(selectedIssue.fields.parent.fields?.issuetype?.name || selectedIssue.fields.parent.issuetype?.name) || 'Task'}
                          className="parent-icon"
                        />
                        <div className="parent-content">
                          <div className="parent-name">
                            {obfuscate(selectedIssue.fields.parent.fields?.summary || selectedIssue.fields.parent.summary) || selectedIssue.fields.parent.key || 'Untitled'}
                          </div>
                          <div className="parent-meta">
                            <span className="parent-key">{selectedIssue.fields.parent.key}</span>
                            {(selectedIssue.fields.parent.fields?.priority || selectedIssue.fields.parent.priority) && (
                              <span className="parent-priority">
                                <span className="priority-bar" style={{
                                  display: 'inline-block',
                                  width: '3px',
                                  height: '12px',
                                  borderRadius: '2px',
                                  marginRight: '4px',
                                  background: (selectedIssue.fields.parent.fields?.priority?.name || selectedIssue.fields.parent.priority?.name) === 'Highest' ? '#ef4056' :
                                             (selectedIssue.fields.parent.fields?.priority?.name || selectedIssue.fields.parent.priority?.name) === 'High' ? '#ff8b00' :
                                             (selectedIssue.fields.parent.fields?.priority?.name || selectedIssue.fields.parent.priority?.name) === 'Medium' ? '#c77800' :
                                             (selectedIssue.fields.parent.fields?.priority?.name || selectedIssue.fields.parent.priority?.name) === 'Low' ? '#3b82f6' : '#7aa2ff'
                                }}></span>
                              </span>
                            )}
                            {(selectedIssue.fields.parent.fields?.status || selectedIssue.fields.parent.status) && (
                              <span
                                className="issue-status"
                                data-status={(selectedIssue.fields.parent.fields?.status?.name || selectedIssue.fields.parent.status?.name) || 'Unknown'}
                              >
                                {(selectedIssue.fields.parent.fields?.status?.name || selectedIssue.fields.parent.status?.name) || 'Unknown'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="parent-action" onClick={(e) => {
                        e.stopPropagation();
                        setParentPopupOpen(true);
                      }}>⋯</div>
                    </div>
                  ) : (
                    <div className="parent-item parent-none" onClick={() => setParentPopupOpen(true)}>
                      <div className="parent-content">
                        <div className="parent-name">None</div>
                      </div>
                      <div className="parent-action">⋯</div>
                    </div>
                  )}
                </div>
              </section>

              {/* Child Work Items Card */}
              <section className={`issue-card ${expandedCards['children'] ? 'expanded' : ''}`}>
                <div className="issue-card-header" onClick={() => {
                  toggleCard('children');
                  // Debug: log subtasks data
                  console.log('Subtasks:', selectedIssue?.fields?.subtasks);
                  if (selectedIssue?.fields?.subtasks?.[0]) {
                    console.log('First subtask structure:', selectedIssue.fields.subtasks[0]);
                  }
                }}>
                  <div className="issue-card-header-content">
                    <div className="issue-card-header-title">Child work items</div>
                    <div className="issue-card-header-summary">
                      {selectedIssue?.fields?.subtasks?.length > 0
                        ? `${selectedIssue.fields.subtasks.length} item${selectedIssue.fields.subtasks.length !== 1 ? 's' : ''}`
                        : 'None'}
                    </div>
                    {!expandedCards['children'] && selectedIssue?.fields?.subtasks?.length > 0 && (
                      <div className="children-progress-bar">
                        {(() => {
                          // Helper function to get status color
                          const getStatusColor = (statusName) => {
                            const status = (statusName || '').toLowerCase();
                            if (status.includes('done') || status.includes('closed') ||
                                status.includes('resolved') || status.includes('complete') ||
                                status.includes('hold')) {
                              return '#10b981'; // Green
                            } else if (status.includes('progress') || status.includes('review') ||
                                      status.includes('development')) {
                              return '#3b82f6'; // Blue
                            } else {
                              return '#6b7280'; // Grey (todo, backlog, open, scoped)
                            }
                          };

                          // Sort subtasks by status (done first, then in progress, then todo)
                          const sortedSubtasks = [...selectedIssue.fields.subtasks].sort((a, b) => {
                            const statusA = (a.fields?.status?.name || a.status?.name || '').toLowerCase();
                            const statusB = (b.fields?.status?.name || b.status?.name || '').toLowerCase();

                            const getStatusOrder = (status) => {
                              if (status.includes('done') || status.includes('closed') ||
                                  status.includes('resolved') || status.includes('complete') ||
                                  status.includes('hold')) return 0;
                              if (status.includes('progress') || status.includes('review') ||
                                  status.includes('development')) return 1;
                              return 2;
                            };

                            return getStatusOrder(statusA) - getStatusOrder(statusB);
                          });

                          const totalSubtasks = sortedSubtasks.length;
                          const segmentWidth = `${100 / totalSubtasks}%`;

                          return sortedSubtasks.map((subtask, index) => {
                            const statusName = subtask.fields?.status?.name || subtask.status?.name || 'Unknown';
                            const color = getStatusColor(statusName);

                            return (
                              <div
                                key={subtask.key || index}
                                className="progress-segment"
                                style={{
                                  width: segmentWidth,
                                  background: color
                                }}
                              />
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                  <span className="chev">▼</span>
                </div>
                <div className="issue-card-body">
                  {selectedIssue?.fields?.subtasks?.length > 0 ? (
                    <>
                      {(() => {
                        // Sort subtasks by status before rendering
                        const sortedSubtasks = [...selectedIssue.fields.subtasks].sort((a, b) => {
                          const statusA = (a.fields?.status?.name || a.status?.name || '').toLowerCase();
                          const statusB = (b.fields?.status?.name || b.status?.name || '').toLowerCase();

                          const getStatusOrder = (status) => {
                            if (status.includes('done') || status.includes('closed') ||
                                status.includes('resolved') || status.includes('complete') ||
                                status.includes('hold')) return 0;
                            if (status.includes('progress') || status.includes('review') ||
                                status.includes('development')) return 1;
                            return 2;
                          };

                          return getStatusOrder(statusA) - getStatusOrder(statusB);
                        });

                        return sortedSubtasks.map((subtask) => (
                        <div
                          key={subtask.key || subtask.id}
                          className="parent-item"
                          onClick={() => {
                            // Open the subtask issue using navigateToIssue
                            const fullSubtask = allIssues.find(i => i.key === subtask.key);
                            navigateToIssue(subtask.key, fullSubtask);
                          }}
                        >
                          <div className="parent-item-main">
                            <img
                              src={`/assets/issue-types/${
                                (subtask.fields?.issuetype?.name || subtask.issuetype?.name) === 'Sub-task' ? 'subtask' :
                                (subtask.fields?.issuetype?.name || subtask.issuetype?.name) === 'Goal' ? 'goal' :
                                'task'
                              }.svg`}
                              alt={(subtask.fields?.issuetype?.name || subtask.issuetype?.name) || 'Task'}
                              className="parent-icon"
                            />
                            <div className="parent-content">
                              <div className="parent-name">
                                {obfuscate(subtask.fields?.summary || subtask.summary) || subtask.key || 'Untitled'}
                              </div>
                              <div className="parent-meta">
                                <span className="parent-key">{subtask.key}</span>
                                {(subtask.fields?.priority || subtask.priority) && (
                                  <span className="parent-priority">
                                    <span className="priority-bar" style={{
                                      display: 'inline-block',
                                      width: '3px',
                                      height: '12px',
                                      borderRadius: '2px',
                                      marginRight: '4px',
                                      background: (subtask.fields?.priority?.name || subtask.priority?.name) === 'Highest' ? '#ef4056' :
                                                 (subtask.fields?.priority?.name || subtask.priority?.name) === 'High' ? '#ff8b00' :
                                                 (subtask.fields?.priority?.name || subtask.priority?.name) === 'Medium' ? '#c77800' :
                                                 (subtask.fields?.priority?.name || subtask.priority?.name) === 'Low' ? '#3b82f6' : '#7aa2ff'
                                    }}></span>
                                  </span>
                                )}
                                {(subtask.fields?.status || subtask.status) && (
                                  <span
                                    className="issue-status"
                                    data-status={(subtask.fields?.status?.name || subtask.status?.name) || 'Unknown'}
                                  >
                                    {(subtask.fields?.status?.name || subtask.status?.name) || 'Unknown'}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        ));
                      })()}
                      <button
                        className="create-child-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // TODO: Open create child dialog
                          console.log('Create child work item for', selectedIssue.key);
                        }}
                      >
                        + Create child work item
                      </button>
                    </>
                  ) : (
                    <div className="no-children">
                      <div className="parent-item parent-none">
                        <div className="parent-content">
                          <div className="parent-name">No child work items</div>
                        </div>
                      </div>
                      <button
                        className="create-child-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          // TODO: Open create child dialog
                          console.log('Create child work item for', selectedIssue.key);
                        }}
                      >
                        + Create child work item
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {/* Details Card */}
              <section className={`issue-card ${expandedCards['details'] ? 'expanded' : ''}`}>
                <div className="issue-card-header" onClick={() => toggleCard('details')}>
                  <div className="issue-card-header-content">
                    <div className="issue-card-header-title">Details</div>
                    <div className="issue-card-header-summary">
                      Issue Type, Reporter
                    </div>
                  </div>
                  <span className="chev">▼</span>
                </div>
                <div className="issue-card-body">
                  {/* Issue Type with icon */}
                  <div className="card-row" style={{cursor: 'pointer'}} onClick={() => setIssueTypePopupOpen(true)}>
                    <div className="row-left">
                      <div className="row-label">Issue Type</div>
                      <div className="row-value">
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <img
                            src={`/assets/issue-types/${
                              selectedIssue?.fields?.issuetype?.name === 'Sub-task' ? 'subtask' :
                              selectedIssue?.fields?.issuetype?.name === 'Goal' ? 'goal' :
                              'task'
                            }.svg`}
                            alt={selectedIssue?.fields?.issuetype?.name || 'Task'}
                            style={{width: '18px', height: '18px'}}
                          />
                          <span>{selectedIssue?.fields?.issuetype?.name || '-'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="row-action">Change</div>
                  </div>

                  {/* Need to Know */}
                  <div
                    className="card-row"
                    style={{cursor: 'pointer'}}
                    onClick={() => {
                      // Start the popup from Jira's actual field value, rather than
                      // from an empty local selection.
                      setNeedToKnowSelected(
                        needToKnowUsers
                          .map((person) => person?.accountId)
                          .filter(Boolean)
                      );
                      setNeedToKnowPopupOpen(true);
                    }}
                  >
                    <div className="row-left">
                      <div className="row-label">Need to Know</div>
                      <div className={`row-value ${needToKnowUsers.length ? '' : 'none-value'}`} style={{whiteSpace: 'normal'}}>
                        {needToKnowUsers.length
                          ? needToKnowUsers.map((person) => person.displayName || person.accountId || 'Unknown user').join(', ')
                          : 'None'}
                      </div>
                    </div>
                    <div className="row-action">Edit</div>
                  </div>

                  {/* Original estimate */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Original estimate</div>
                      <div className="row-value none-value">None</div>
                    </div>
                    <div className="row-action">Edit</div>
                  </div>

                  {/* Days Late */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Days Late</div>
                      <div className="row-value">0</div>
                    </div>
                  </div>

                  {/* Assigned group */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Assigned group</div>
                      <div className="row-value none-value">None</div>
                    </div>
                    <div className="row-action">Edit</div>
                  </div>
                </div>
              </section>

              {/* More fields Card */}
              <section className={`issue-card ${expandedCards['more'] ? 'expanded' : ''}`}>
                <div className="issue-card-header" onClick={() => toggleCard('more')}>
                  <div className="issue-card-header-content">
                    <div className="issue-card-header-title">More fields</div>
                    <div className="issue-card-header-summary">
                      Project, Created, Updated
                    </div>
                  </div>
                  <span className="chev">▼</span>
                </div>
                <div className="issue-card-body">
                  {/* Project */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Project</div>
                      <div className="row-value">
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                          <img
                            src="/assets/project-icon.svg"
                            alt={selectedIssue?.fields?.project?.name || 'Project'}
                            style={{width: '18px', height: '18px'}}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <span>{selectedIssue?.fields?.project?.name || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Created */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Created</div>
                      <div className="row-value">
                        {selectedIssue?.fields?.created
                          ? new Date(selectedIssue.fields.created).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })
                          : '-'}
                      </div>
                    </div>
                  </div>

                  {/* Updated */}
                  <div className="card-row">
                    <div className="row-left">
                      <div className="row-label">Updated</div>
                      <div className="row-value">
                        {selectedIssue?.fields?.updated
                          ? new Date(selectedIssue.fields.updated).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Feed Section with Comments/History Toggle */}
              <div className="issue-feed-header">
                <button className="feed-toggle-btn">
                  Comments <span className="caret">▼</span>
                </button>
                <button className="feed-toggle-btn">
                  Newest first <span className="caret">▼</span>
                </button>
              </div>

              <div className="feed-content">
                {commentsLoading && issueComments.length === 0 ? (
                  <div style={{textAlign: 'center', padding: '20px', color: 'var(--text-secondary, #666)'}}>
                    Loading comments...
                  </div>
                ) : (
                  <>
                    {issueComments.map((comment) => (
                      <div key={comment.id} className="feed-item" style={{position: 'relative'}}>
                        {/* Floating avatar */}
                        {comment.author?.avatarUrls?.['24x24'] ? (
                          <img
                            src={comment.author.avatarUrls['24x24']}
                            alt={comment.author.displayName}
                            className="comment-avatar"
                          />
                        ) : (
                          <div className="comment-avatar">
                            {comment.author?.displayName?.[0]?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div className="feed-item-header">
                          <span className="feed-author">
                            {comment.author?.displayName || 'Unknown'}
                          </span>
                          <span className="feed-meta">
                            {new Date(comment.created).toLocaleDateString()} · {new Date(comment.created).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                          </span>
                        </div>
                        <RichTextHtml
                          className="feed-body rich-text"
                          html={(() => {
                            let html = comment.renderedBody || convertADFToHTML(comment.body) || 'No content';
                            // Make relative attachment URLs absolute
                            const jiraRoot = (process.env.REACT_APP_JIRA_BASE_URL || '').replace(/\/browse\/?$/, '');
                            html = html.replace(
                              /src="\/rest\/api\/3\/attachment\/content\//g,
                              `src="${jiraRoot}/rest/api/3/attachment/content/`
                            );
                            return html;
                          })()}
                        />
                      </div>
                    ))}

                    {/* Load more button */}
                    {commentsStartAt < commentsTotal && (
                      <button
                        className="load-more-comments"
                        onClick={() => fetchComments(selectedIssue.key, commentsStartAt)}
                        disabled={commentsLoading}
                      >
                        {commentsLoading ? 'Loading...' : 'Load More Comments'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
            )}

            {/* Fixed Bottom Composer - only show when we have issue data */}
            {!issueError && selectedIssue?.fields && (
            <div className="issue-composer">
              <div className="composer-field">
                <input placeholder="Add a comment…" />
                <button className="composer-send-btn">Send</button>
              </div>
            </div>
            )}
          </div>
        </div>
      )}
      
      {/* AI Recording Modal */}
      <RecordingModal 
        isOpen={recordingPopupOpen}
        onClose={() => setRecordingPopupOpen(false)}
        context={recordingContext}
        onTranscriptReceived={handleTranscriptReceived}
        user={user}
      />
      
      {/* Transition popup */}
      {transitionIssue && (
        <div className="transition-popup-overlay" onClick={() => !loadingTransitions && setTransitionIssue(null)}>
          <div className="transition-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Move {transitionIssue.key}</h3>
              <button 
                className="close-btn"
                onClick={() => setTransitionIssue(null)}
                disabled={loadingTransitions}
              >
                ✕
              </button>
            </div>
            
            <div className="transition-content">
              {loadingTransitions ? (
                <div className="loading">Loading transitions...</div>
              ) : availableTransitions.length > 0 ? (
                <div className="transition-list">
                  {availableTransitions.map(transition => {
                    return (
                      <button
                        key={transition.id}
                        className="transition-option"
                        onClick={async () => {
                          try {
                            setLoadingTransitions(true);
                            const response = await fetch(`/api/jira/transitions/${transitionIssue.key}`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: getAuthHeader()
                              },
                              body: JSON.stringify({ transitionId: transition.id })
                            });

                            if (response.ok) {
                              // Refresh cache to get updated issue status
                              refresh();
                              // Mark as recently accessed
                              markIssueAsRecent(transitionIssue.key);
                              setTransitionIssue(null);
                              setAvailableTransitions([]);
                            } else {
                              console.error('Failed to transition issue');
                              alert('Failed to transition issue');
                            }
                          } catch (error) {
                            console.error('Error transitioning issue:', error);
                            alert('Error transitioning issue');
                          } finally {
                            setLoadingTransitions(false);
                          }
                        }}
                        disabled={loadingTransitions}
                      >
                        <span className="issue-status" data-status={transition.to?.name || transition.name}>
                          {(transition.to?.name || transition.name).toUpperCase()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="no-transitions">No transitions available</div>
              )}
              
              <button 
                className="cancel-btn"
                onClick={() => setTransitionIssue(null)}
                disabled={loadingTransitions}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parent work item popup */}
      {parentPopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setParentPopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Change parent</h3>
              <button
                className="close-btn"
                onClick={() => setParentPopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="parent-search-box">
              <input
                type="text"
                placeholder="Search issues..."
                value={parentSearchQuery}
                onChange={(e) => {
                  const query = e.target.value;
                  setParentSearchQuery(query);

                  // Filter top 100 issues by search query
                  if (query.trim()) {
                    const filtered = allIssues
                      .filter(issue =>
                        issue.key.toLowerCase().includes(query.toLowerCase()) ||
                        issue.fields?.summary?.toLowerCase().includes(query.toLowerCase())
                      )
                      .slice(0, 100);
                    setParentSearchResults(filtered);
                  } else {
                    setParentSearchResults(allIssues.slice(0, 100));
                  }
                }}
              />
            </div>

            <div className="transition-content parent-list-content">
              <div className="parent-issue-list">
                {/* Option to remove parent - always visible */}
                <button
                  className={`parent-issue-option ${!selectedIssue?.fields?.parent ? 'parent-selected' : ''}`}
                  onClick={() => saveIssueFields({ parent: null }, () => setParentPopupOpen(false))}
                >
                  <div className="parent-option-content">
                    <div className="parent-option-name">None</div>
                    <div className="parent-option-meta">No parent</div>
                  </div>
                </button>

                {/* Show search results or all issues */}
                {(() => {
                  const issueList = parentSearchQuery.trim() ? parentSearchResults : allIssues.slice(0, 100);

                  if (issueList.length > 0) {
                    // Sort to show current parent first, then rest
                    return issueList
                      .sort((a, b) => {
                        const aIsCurrent = selectedIssue?.fields?.parent?.key === a.key;
                        const bIsCurrent = selectedIssue?.fields?.parent?.key === b.key;
                        if (aIsCurrent && !bIsCurrent) return -1;
                        if (!aIsCurrent && bIsCurrent) return 1;
                        return 0;
                      })
                      .map(issue => (
                        <button
                          key={issue.key}
                          className={`parent-issue-option ${selectedIssue?.fields?.parent?.key === issue.key ? 'parent-selected' : ''}`}
                          onClick={() => saveIssueFields({ parent: issue.key }, () => setParentPopupOpen(false))}
                        >
                          <img
                            src={`/assets/issue-types/${
                              issue.fields?.issuetype?.name === 'Sub-task' ? 'subtask' :
                              issue.fields?.issuetype?.name === 'Goal' ? 'goal' :
                              'task'
                            }.svg`}
                            alt={issue.fields?.issuetype?.name || 'Task'}
                            className="parent-option-icon"
                          />
                          <div className="parent-option-content">
                            <div className="parent-option-name">
                              {obfuscate(issue.fields?.summary) || 'Untitled'}
                            </div>
                            <div className="parent-option-meta">
                              <span className="parent-option-key">{issue.key}</span>
                              <span className="parent-option-priority">
                                <span className="priority-bar" style={{
                                  display: 'inline-block',
                                  width: '3px',
                                  height: '12px',
                                  borderRadius: '2px',
                                  marginRight: '4px',
                                  background: issue.fields?.priority?.name === 'Highest' ? '#ef4056' :
                                             issue.fields?.priority?.name === 'High' ? '#ff8b00' :
                                             issue.fields?.priority?.name === 'Medium' ? '#c77800' :
                                             issue.fields?.priority?.name === 'Low' ? '#3b82f6' : '#7aa2ff'
                                }}></span>
                              </span>
                              <span
                                className="issue-status"
                                data-status={issue.fields?.status?.name || 'Unknown'}
                              >
                                {issue.fields?.status?.name || 'Unknown'}
                              </span>
                            </div>
                          </div>
                        </button>
                      ));
                  } else {
                    // No issues found in search
                    return <div className="no-transitions">No issues found</div>;
                  }
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign issue popup */}
      {assigneePopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setAssigneePopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Assign issue</h3>
              <button
                className="close-btn"
                onClick={() => setAssigneePopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="transition-content parent-list-content">
              <div className="parent-issue-list">
                {(() => {
                  // Get unique users from all issues
                  const usersMap = new Map();

                  availableUsers.forEach((user) => usersMap.set(user.accountId, user));

                  allIssues.forEach(issue => {
                    const assignee = issue.fields?.assignee;
                    if (assignee && assignee.accountId) {
                      usersMap.set(assignee.accountId, {
                        accountId: assignee.accountId,
                        displayName: assignee.displayName,
                        avatarUrl: assignee.avatarUrls?.['24x24'] || assignee.avatarUrls?.['48x48']
                      });
                    }
                  });

                  const users = Array.from(usersMap.values()).sort((a, b) =>
                    a.displayName.localeCompare(b.displayName)
                  );

                  const currentAssigneeId = selectedIssue?.fields?.assignee?.accountId;

                  return users.map(user => (
                    <button
                      key={user.accountId}
                      className={`parent-issue-option ${currentAssigneeId === user.accountId ? 'parent-selected' : ''}`}
                      onClick={() => saveIssueFields({ assignee: user.accountId }, () => setAssigneePopupOpen(false))}
                    >
                      <div className="parent-option-content" style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        {user.avatarUrl ? (
                          <img
                            src={user.avatarUrl}
                            alt={user.displayName}
                            style={{width: '28px', height: '28px', borderRadius: '50%'}}
                          />
                        ) : (
                          <div style={{
                            width: '28px', height: '28px', borderRadius: '50%',
                            background: 'var(--avatar-background, #2b2f38)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '12px', fontWeight: '700', color: '#fff'
                          }}>
                            {user.displayName[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="parent-option-name">{user.displayName}</div>
                        </div>
                      </div>
                      {currentAssigneeId === user.accountId && (
                        <div style={{color: 'var(--primary-color, #007bff)', fontSize: '18px'}}>✓</div>
                      )}
                    </button>
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Priority popup */}
      {priorityPopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setPriorityPopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Priority</h3>
              <button
                className="close-btn"
                onClick={() => setPriorityPopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="transition-content parent-list-content">
              <div className="parent-issue-list">
                {['Highest', 'High', 'Medium', 'Low', 'Lowest'].map(priority => {
                  const currentPriority = selectedIssue?.fields?.priority?.name || 'Medium';
                  const isSelected = currentPriority === priority;
                  const priorityLower = priority.toLowerCase();

                  let iconElement, colorClass;
                  if (priorityLower === 'highest') {
                    iconElement = (
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                        <path d="M4 11L8 7L12 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4 7L8 3L12 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    );
                    colorClass = 'priority-highest';
                  } else if (priorityLower === 'high') {
                    iconElement = (
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                        <path d="M4 10L8 6L12 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    );
                    colorClass = 'priority-high';
                  } else if (priorityLower === 'medium') {
                    iconElement = '═';
                    colorClass = 'priority-medium';
                  } else if (priorityLower === 'low') {
                    iconElement = (
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    );
                    colorClass = 'priority-low';
                  } else {
                    iconElement = (
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                        <path d="M4 5L8 9L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M4 9L8 13L12 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    );
                    colorClass = 'priority-lowest';
                  }

                  return (
                    <button
                      key={priority}
                      className={`parent-issue-option ${isSelected ? 'parent-selected' : ''}`}
                      onClick={() => saveIssueFields({ priority }, () => setPriorityPopupOpen(false))}
                    >
                      <div className="parent-option-content" style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <span className={`priority-icon ${colorClass}`} style={{fontSize: '18px', display: 'flex', alignItems: 'center'}}>{iconElement}</span>
                        <div>
                          <div className="parent-option-name">{priority}</div>
                        </div>
                      </div>
                      {isSelected && (
                        <div style={{color: 'var(--primary-color, #007bff)', fontSize: '18px'}}>✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue Type popup */}
      {issueTypePopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setIssueTypePopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Issue Type</h3>
              <button
                className="close-btn"
                onClick={() => setIssueTypePopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="transition-content parent-list-content">
              <div className="parent-issue-list">
                {availableIssueTypes.map(issueType => {
                  const currentType = selectedIssue?.fields?.issuetype?.name || 'Task';
                  const isSelected = currentType === issueType.name;

                  const iconName = issueType.subtask ? 'subtask' :
                                   issueType.name === 'Goal' ? 'goal' :
                                   'task';

                  return (
                    <button
                      key={issueType.id}
                      className={`parent-issue-option ${isSelected ? 'parent-selected' : ''}`}
                      onClick={() => saveIssueFields({ issueType: issueType.name }, () => setIssueTypePopupOpen(false))}
                    >
                      <div className="parent-option-content" style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <img
                          src={`/assets/issue-types/${iconName}.svg`}
                          alt={issueType.name}
                          style={{width: '18px', height: '18px'}}
                        />
                        <div>
                          <div className="parent-option-name">{issueType.name}</div>
                        </div>
                      </div>
                      {isSelected && (
                        <div style={{color: 'var(--primary-color, #007bff)', fontSize: '18px'}}>✓</div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Need to Know popup */}
      {needToKnowPopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setNeedToKnowPopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Need To Know</h3>
              <button
                className="close-btn"
                onClick={() => setNeedToKnowPopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="transition-content parent-list-content">
              <div className="parent-issue-list">
                {(() => {
                  // Include the issue's existing Need To Know members first. They
                  // may not be assignees on any currently loaded issue.
                  const usersMap = new Map();

                  availableUsers.forEach((user) => usersMap.set(user.accountId, user));

                  needToKnowUsers.forEach(person => {
                    if (person?.accountId) {
                      usersMap.set(person.accountId, {
                        accountId: person.accountId,
                        displayName: person.displayName || person.accountId,
                        avatarUrl: person.avatarUrls?.['24x24'] || person.avatarUrls?.['48x48']
                      });
                    }
                  });

                  allIssues.forEach(issue => {
                    const assignee = issue.fields?.assignee;
                    if (assignee && assignee.accountId) {
                      usersMap.set(assignee.accountId, {
                        accountId: assignee.accountId,
                        displayName: assignee.displayName,
                        avatarUrl: assignee.avatarUrls?.['24x24'] || assignee.avatarUrls?.['48x48']
                      });
                    }
                  });

                  const users = Array.from(usersMap.values()).sort((a, b) =>
                    (a.displayName || a.accountId).localeCompare(b.displayName || b.accountId)
                  );

                  return users.map(user => {
                    const isSelected = needToKnowSelected.includes(user.accountId);

                    return (
                      <button
                        key={user.accountId}
                        className={`parent-issue-option ${isSelected ? 'parent-selected' : ''}`}
                        onClick={() => {
                          // Toggle selection
                          setNeedToKnowSelected(prev => {
                            if (prev.includes(user.accountId)) {
                              return prev.filter(id => id !== user.accountId);
                            } else {
                              return [...prev, user.accountId];
                            }
                          });
                        }}
                      >
                        <div className="parent-option-content" style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt={user.displayName}
                              style={{width: '28px', height: '28px', borderRadius: '50%'}}
                            />
                          ) : (
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%',
                              background: 'var(--avatar-background, #2b2f38)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '12px', fontWeight: '700', color: '#fff'
                            }}>
                              {(user.displayName || user.accountId || '?')[0].toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="parent-option-name">{user.displayName}</div>
                          </div>
                        </div>
                        {isSelected && (
                          <div style={{color: 'var(--primary-color, #007bff)', fontSize: '18px'}}>✓</div>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Save button */}
            <div style={{padding: '12px 16px', borderTop: '1px solid var(--border-color, #e0e0e0)'}}>
              <button
                className="primary-button"
                style={{width: '100%', padding: '12px', borderRadius: '8px', background: 'var(--primary-color, #007bff)', color: '#fff', border: 'none', fontWeight: '600', cursor: 'pointer'}}
                disabled={issueSaving}
                onClick={() => saveIssueFields(
                  { needToKnowAccountIds: needToKnowSelected },
                  () => setNeedToKnowPopupOpen(false)
                )}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Due Date popup */}
      {dueDatePopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setDueDatePopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Due date</h3>
              <button
                className="close-btn"
                onClick={() => setDueDatePopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="transition-content" style={{padding: '16px'}}>
              <input
                type="date"
                value={editedDueDate}
                onChange={(e) => setEditedDueDate(e.target.value)}
                className="popup-input"
              />
              <div style={{display: 'flex', gap: '10px', marginTop: '16px'}}>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #e0e0e0)',
                    background: 'transparent',
                    color: 'var(--text-primary, #333)',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  onClick={() => setDueDatePopupOpen(false)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'var(--primary-color, #007bff)',
                    color: '#fff',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  disabled={issueSaving}
                  onClick={() => saveIssueFields({ dueDate: editedDueDate || null }, () => setDueDatePopupOpen(false))}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue Title Edit popup */}
      {issueTitlePopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setIssueTitlePopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Edit issue name</h3>
              <button
                className="close-btn"
                onClick={() => setIssueTitlePopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="transition-content" style={{padding: '16px'}}>
              <input
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                placeholder="Issue summary"
                autoFocus
                className="popup-input"
              />
              <div style={{display: 'flex', gap: '10px', marginTop: '16px'}}>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #e0e0e0)',
                    background: 'transparent',
                    color: 'var(--text-primary, #333)',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  onClick={() => setIssueTitlePopupOpen(false)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'var(--primary-color, #007bff)',
                    color: '#fff',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  disabled={issueSaving}
                  onClick={() => saveIssueFields({ summary: editedTitle }, () => setIssueTitlePopupOpen(false))}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Edit popup */}
      {descriptionPopupOpen && (
        <div className="transition-popup-overlay" onClick={() => setDescriptionPopupOpen(false)}>
          <div className="transition-popup parent-popup" onClick={(e) => e.stopPropagation()}>
            <div className="transition-header">
              <h3>Edit description</h3>
              <button
                className="close-btn"
                onClick={() => setDescriptionPopupOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="transition-content" style={{padding: '16px'}}>
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                placeholder="Add a description..."
                autoFocus
                rows={12}
                className="popup-input"
                style={{resize: 'vertical'}}
              />
              <div style={{display: 'flex', gap: '10px', marginTop: '16px'}}>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #e0e0e0)',
                    background: 'transparent',
                    color: 'var(--text-primary, #333)',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  onClick={() => setDescriptionPopupOpen(false)}
                >
                  Cancel
                </button>
                <button
                  style={{
                    flex: 1,
                    padding: '12px',
                    borderRadius: '8px',
                    background: 'var(--primary-color, #007bff)',
                    color: '#fff',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                  disabled={issueSaving}
                  onClick={() => saveIssueFields({ description: editedDescription }, () => setDescriptionPopupOpen(false))}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating drag preview */}
      {isDraggingSort && draggedIssue && (
        <div
          className="drag-preview"
          style={{
            position: 'fixed',
            left: dragPosition.x - 100,
            top: dragPosition.y - 20,
            width: '200px',
            pointerEvents: 'none',
            zIndex: 10000,
            transform: 'rotate(2deg)',
            opacity: 0.9
          }}
        >
          <div className={`jira-issue-row ${pinnedIssues.includes(draggedIssue.key) ? 'pinned' : ''} ${issueColors[draggedIssue.key] ? `color-${issueColors[draggedIssue.key].toLowerCase()}` : ''}`}
            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
          >
            {/* Issue type icon */}
            <img
              src={`/assets/issue-types/${
                draggedIssue.fields?.issuetype?.name === 'Sub-task' ? 'subtask' :
                draggedIssue.fields?.issuetype?.name === 'Goal' ? 'goal' :
                'task'
              }.svg`}
              alt={draggedIssue.fields?.issuetype?.name || 'Task'}
              className="issue-type-icon"
            />

            {/* Pin icon */}
            {pinnedIssues.includes(draggedIssue.key) && <span className="pin-icon">📌</span>}

            <div className="issue-content">
              <div className="issue-title">
                {obfuscate(draggedIssue.fields?.summary) || 'Untitled'}
              </div>
              <div className="issue-meta">
                <span className="issue-key">{draggedIssue.key}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Search helper function
function searchIssues(list, q) {
  if (!q) return list;
  const s = q.toLowerCase();
  return list.filter(it => {
    // Safely convert description to string (it might be an object)
    const desc = typeof it.description === 'string' ? it.description : '';
    const summary = it.fields?.summary || it.summary || '';
    const status = it.fields?.status?.name || it.status || '';
    const priority = it.fields?.priority?.name || it.priority || '';
    const assignee = it.fields?.assignee?.displayName || it.assignee?.display || '';

    return (
      it.key.toLowerCase().includes(s) ||
      summary.toLowerCase().includes(s) ||
      desc.toLowerCase().includes(s) ||
      it.labels?.some(l => l.toLowerCase().includes(s)) ||
      it.project.toLowerCase().includes(s) ||
      status.toLowerCase().includes(s) ||
      priority.toLowerCase().includes(s) ||
      assignee.toLowerCase().includes(s)
    );
  });
}
